import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Form, Button, Icon, Message, Header, Segment, Modal, Dimmer, Loader, Menu, Table, Label, Checkbox } from 'semantic-ui-react';
import { api } from '../api/apiClient';
import PaginationFooter from './PaginationFooter';
import { useTablePreferences } from '../hooks/useTablePreferences';
import { useDropdownOptions } from '../hooks/useDropdownOptions';

const LICENSE_GENERATOR_CLOUD_CACHE_KEY = 'tenantBridge.licenseGenerator.cloudDirectory.v1';

const readCloudDirectoryCache = () => {
    if (typeof window === 'undefined') {
        return { options: [], meta: {} };
    }
    try {
        const raw = window.localStorage.getItem(LICENSE_GENERATOR_CLOUD_CACHE_KEY);
        if (!raw) return { options: [], meta: {} };
        const parsed = JSON.parse(raw);
        const options = Array.isArray(parsed?.options) ? parsed.options : [];
        const meta = parsed?.meta && typeof parsed.meta === 'object' ? parsed.meta : {};
        return { options, meta };
    } catch {
        return { options: [], meta: {} };
    }
};

const writeCloudDirectoryCache = (options, meta) => {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(
            LICENSE_GENERATOR_CLOUD_CACHE_KEY,
            JSON.stringify({
                options: Array.isArray(options) ? options : [],
                meta: meta && typeof meta === 'object' ? meta : {}
            })
        );
    } catch {
        // ignore
    }
};

function safeCopyToClipboard(text) {
    if (!text) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).catch(() => {
            try {
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            } catch {
                // ignore
            }
        });
        return;
    }
    try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
    } catch {
        // ignore
    }
}

const normalizeAddonDefinition = (addon) => {
    if (!addon) return null;
    if (typeof addon === 'string') {
        return { name: addon, subaddons: [] };
    }
    if (addon.hidden === true) return null;
    const name = addon.name || addon.text || addon.label;
    if (!name) return null;
    const subaddons = (addon.subaddons || []).map((sub) => {
        if (typeof sub === 'string') return { name: sub };
        if (sub?.hidden === true) return null;
        return {
            ...(sub || {}),
            name: sub.name || sub.text || sub.label,
            apiKey: sub.apiKey
        };
    }).filter((sub) => sub?.name);
    return {
        ...addon,
        name,
        subaddons
    };
};

/** Build UI + API catalog from dropdown options (license_products group). */
const isMicrosoftEntraAddon = (addonDef, addonName) =>
    addonDef?.apiProductName === 'microsoftEntra' ||
    addonName === 'Microsoft Entra' ||
    String(addonDef?.name || '').trim() === 'Microsoft Entra';

const MICROSOFT_ENTRA_PRODUCT_CONFIG = {
    addons: {},
    addonDefinitions: [],
    // Same provisioning category/productName as when Entra was an M365 add-on.
    apiCategory: 'm365Enterprise',
    selectionMode: 'productQuantity',
    apiProductName: 'microsoftEntra',
    defaultAddonApiKeys: []
};

const buildProductCatalogFromOptions = (options = []) => {
    const catalog = {};
    options.forEach((option) => {
        const productName = option.value || option.text;
        if (!productName) return;

        // Skip inactive/stale standalone Entra rows from DB; we inject a fixed UI product below.
        if (productName === 'Microsoft Entra') return;

        let addonDefinitions = (option.addons || [])
            .map(normalizeAddonDefinition)
            .filter(Boolean)
            .map((addon) => {
                // Strip any stale Storage (GB) config for Microsoft Entra.
                if (isMicrosoftEntraAddon(addon, addon.name)) {
                    const { hasStorageGb, storageField, ...rest } = addon;
                    return rest;
                }
                return addon;
            });

        // UI: Entra is a standalone product; never keep it under M365 add-ons.
        if (productName === 'M365 Enterprise') {
            addonDefinitions = addonDefinitions.filter(
                (addon) => !isMicrosoftEntraAddon(addon, addon.name)
            );
        }

        const addons = {};
        addonDefinitions.forEach((addon) => {
            addons[addon.name] = (addon.subaddons || []).map((sub) => sub.name);
        });
        const visibleAddonApiKeys = new Set(
            addonDefinitions.map((addon) => addon.apiKey).filter(Boolean)
        );

        catalog[productName] = {
            addons,
            addonDefinitions,
            apiCategory: option.apiCategory || null,
            selectionMode: option.selectionMode || 'perAddon',
            apiProductName: option.apiProductName || null,
            defaultAddonApiKeys: Array.isArray(option.defaultAddonApiKeys)
                ? option.defaultAddonApiKeys.filter((apiKey) => visibleAddonApiKeys.has(apiKey))
                : []
        };
    });

    // Place Microsoft Entra directly above M365 Enterprise in the UI.
    const ordered = {};
    let entraInserted = false;
    Object.keys(catalog).forEach((name) => {
        if (name === 'M365 Enterprise' && !entraInserted) {
            ordered['Microsoft Entra'] = { ...MICROSOFT_ENTRA_PRODUCT_CONFIG };
            entraInserted = true;
        }
        ordered[name] = catalog[name];
    });
    if (!entraInserted) {
        ordered['Microsoft Entra'] = { ...MICROSOFT_ENTRA_PRODUCT_CONFIG };
    }

    return ordered;
};

const buildInitialProductSelection = (catalog = {}) => {
    const selection = {};
    Object.keys(catalog).forEach((productName) => {
        selection[productName] = {
            selected: false,
            quantity: '',
            addons: {}
        };
        const addons = catalog[productName].addons || {};
        Object.keys(addons).forEach((addonName) => {
            selection[productName].addons[addonName] = {
                selected: false,
                userCount: '',
                storageGb: '',
                subaddons: {}
            };
            const subaddons = addons[addonName] || [];
            subaddons.forEach((subaddonName) => {
                selection[productName].addons[addonName].subaddons[subaddonName] = false;
            });
        });
    });
    return selection;
};

const getAddonDefinition = (productConfig, addonName) =>
    (productConfig?.addonDefinitions || []).find((addon) => addon.name === addonName) || null;

/** True when an add-on should show/send an independent Storage (GB) field. */
const addonHasStorageGb = (addonDef, addonName) => {
    // Microsoft Entra never uses per-addon Storage (GB).
    if (isMicrosoftEntraAddon(addonDef, addonName)) return false;
    return !!addonDef?.hasStorageGb;
};

const getAddonStorageField = (addonDef) =>
    (addonDef?.storageField && String(addonDef.storageField).trim()) || 'storageGb';

// Renders API response as readable key-value pairs (fallback for generic data)
function FormattedDetailsView({ data, depth = 0 }) {
    if (data === null || data === undefined) {
        return <span style={{ color: '#888' }}>—</span>;
    }
    if (Array.isArray(data)) {
        return (
            <ul style={{ margin: '0.25rem 0', paddingLeft: '1.25rem' }}>
                {data.map((item, idx) => (
                    <li key={idx}>
                        {typeof item === 'object' && item !== null ? (
                            <FormattedDetailsView data={item} depth={depth + 1} />
                        ) : (
                            String(item ?? '')
                        )}
                    </li>
                ))}
            </ul>
        );
    }
    if (typeof data === 'object') {
        const entries = Object.entries(data);
        if (entries.length === 0) return <span style={{ color: '#888' }}>—</span>;
        const paddingLeft = depth > 0 ? '1rem' : '0';
        return (
            <div style={{ paddingLeft, marginTop: depth > 0 ? '0.5rem' : 0 }}>
                {entries.map(([key, value]) => (
                    <div key={key} style={{ marginBottom: '0.5rem' }}>
                        <strong style={{ color: '#062D4D' }}>{key}:</strong>{' '}
                        {typeof value === 'object' && value !== null ? (
                            <FormattedDetailsView data={value} depth={depth + 1} />
                        ) : (
                            <span>{String(value ?? '')}</span>
                        )}
                    </div>
                ))}
            </div>
        );
    }
    return <span>{String(data)}</span>;
}

// Render ALL license file keys, but format values/labels in a human readable way
// (dates, 0/1 enabled flags, etc.), without applying the "skipSet" filtering used by DetailsSection.
function HumanReadableLicenseFileView({ data, maxKeys = 500 }) {
    if (data == null) return <span style={{ color: '#888' }}>—</span>;
    if (typeof data !== 'object' || Array.isArray(data)) return <span>{formatDisplayValue(data)}</span>;

    const entries = Object.entries(data);
    if (entries.length === 0) return <span style={{ color: '#888' }}>—</span>;

    // Put cloudName first if it exists.
    entries.sort((a, b) => {
        const aKey = (a?.[0] ?? '').toString();
        const bKey = (b?.[0] ?? '').toString();
        if (aKey === 'cloudName' && bKey !== 'cloudName') return -1;
        if (bKey === 'cloudName' && aKey !== 'cloudName') return 1;
        return aKey.localeCompare(bKey);
    });

    const parseYesNoToken = (value) => {
        if (value === true) return 'yes';
        if (value === false) return 'no';
        if (value === 1) return 'yes';
        if (value === 0) return 'no';
        if (typeof value === 'string') {
            const v = value.trim().toLowerCase();
            if (v === '1' || v === 'true' || v === 'yes') return 'yes';
            if (v === '0' || v === 'false' || v === 'no') return 'no';
        }
        return null;
    };

    const keyMap = Object.keys(data || {}).reduce((acc, k) => {
        acc[String(k).toLowerCase()] = k;
        return acc;
    }, {});
    const getByAnyKey = (...keys) => {
        for (const k of keys) {
            if (k in (data || {})) return data[k];
            const mapped = keyMap[String(k).toLowerCase()];
            if (mapped && mapped in (data || {})) return data[mapped];
        }
        return undefined;
    };

    const boolOn = (...keys) => {
        const v = getByAnyKey(...keys);
        return v === 1 || v === true || v === '1' || v === 'true';
    };

    const productRows = [];
    const productRelatedKeys = new Set([
        'noOfOdbBackupUsersAllowed',
        'noOfExchangeBackupUsersAllowed',
        'noOfSharepointBackupUsersAllowed',
        'noOfGmailBackupUsersAllowed',
        'noOfGdriveBackupUsersAllowed',
        'noOfServerUsersAllowed',
        'noOfEntraUsersAllowed',
        'noOfMicrosoftEntraUsersAllowed',
        'noOfBackupUsersAllowed',
        'noOfSyncUsersAllowed',
        'noOfMiniCloudsAllowed',
        'EnableContentSearchForOnedrive',
        'OneDriveArchivingEnabled',
        'EnableContentSearchForExchange',
        'MailArchivingEnabled',
        'InplaceArchiveEnabled',
        'EnableContentSearchForSharepoint',
        'SharepointArchivingEnabled',
        'EnableContentSearchForGmail',
        'EnableContentSearchForGdrive',
        'EnableContentSearchForEntra',
        'EnableContentSearchForMicrosoftEntra',
        'enableContentSearchForEndPoint',
        'ServerBackupEnabled',
        'BackupNetworkDriveEnabled'
    ]);

    const pushProductRow = (productName, addonName, quantity, subaddons = []) => {
        const qty = Number(quantity ?? 0);
        if (!Number.isFinite(qty) || qty <= 0) return;
        productRows.push({
            productName,
            addonName,
            quantity: String(qty),
            subaddons
        });
    };

    pushProductRow('M365 Enterprise', 'OneDrive', getByAnyKey('noOfOdbBackupUsersAllowed', 'noOfOneDriveBackupUsersAllowed'), [
        ...(boolOn('EnableContentSearchForOnedrive', 'EnableContentSearchForOneDrive') ? ['eDiscovery / Search'] : []),
        ...(boolOn('OneDriveArchivingEnabled') ? ['Capacity Savings'] : [])
    ]);
    pushProductRow('M365 Enterprise', 'Exchange', getByAnyKey('noOfExchangeBackupUsersAllowed'), [
        ...(boolOn('EnableContentSearchForExchange') ? ['eDiscovery / Search'] : []),
        ...(boolOn('MailArchivingEnabled') ? ['Capacity Savings'] : []),
        ...(boolOn('InplaceArchiveEnabled') ? ['In-Place Archive'] : [])
    ]);
    pushProductRow('M365 Enterprise', 'SharePoint', getByAnyKey('noOfSharepointBackupUsersAllowed', 'noOfSharePointBackupUsersAllowed'), [
        ...(boolOn('EnableContentSearchForSharepoint', 'EnableContentSearchForSharePoint') ? ['eDiscovery / Search'] : []),
        ...(boolOn('SharepointArchivingEnabled') ? ['Capacity Savings'] : [])
    ]);
    pushProductRow('Microsoft Entra', 'Microsoft Entra', getByAnyKey('noOfEntraUsersAllowed', 'noOfMicrosoftEntraUsersAllowed'), [
        ...(boolOn('EnableContentSearchForEntra', 'EnableContentSearchForMicrosoftEntra') ? ['eDiscovery / Search'] : [])
    ]);
    pushProductRow('Google Workspace', 'Gmail', getByAnyKey('noOfGmailBackupUsersAllowed'), [
        ...(boolOn('EnableContentSearchForGmail') ? ['eDiscovery / Search'] : [])
    ]);
    pushProductRow('Google Workspace', 'GDrive', getByAnyKey('noOfGdriveBackupUsersAllowed', 'noOfGDriveBackupUsersAllowed'), [
        ...(boolOn('EnableContentSearchForGdrive', 'EnableContentSearchForGDrive') ? ['eDiscovery / Search'] : [])
    ]);
    pushProductRow('Server', 'Server backup', getByAnyKey('noOfServerUsersAllowed'), [
        ...(boolOn('ServerBackupEnabled') ? ['SQL Backup'] : []),
        ...(boolOn('BackupNetworkDriveEnabled') ? ['Backup Network Drives'] : [])
    ]);

    // Render Endpoint / MFT in the same grouped style as other products.
    const endpointSubaddons = [];
    if (boolOn('c2cRestoreEnabled')) endpointSubaddons.push('Cloud to Cloud (C2C) Restore');
    if (boolOn('RWLicenseAllowed')) endpointSubaddons.push('Ransomware Defense Suite (RDS)');
    if (boolOn('isEndpointRestoreAVScanEnabled')) endpointSubaddons.push('Anti-Malware Scanning');
    if (boolOn('enableContentSearchForEndPoint')) {
        endpointSubaddons.push('eDiscovery / Search');
    }
    pushProductRow('Endpoint', 'Endpoint backup', getByAnyKey('noOfBackupUsersAllowed'), endpointSubaddons);
    {
        const mftSubaddons = [];
        const miniCloudQty = Number(getByAnyKey('noOfMiniCloudsAllowed') ?? 0);
        if (miniCloudQty > 0) mftSubaddons.push(`MiniClouds (Qty:${miniCloudQty})`);
        if (boolOn('ClamAVLicenseAllowed')) mftSubaddons.push('Anti-Malware Scanning');
        if (boolOn('EnableContentSearchForSync')) mftSubaddons.push('eDiscovery / Search');
        pushProductRow('Managed File Transfer (MFT / BluSync)', 'MFT (Managed File Transfer)', getByAnyKey('noOfSyncUsersAllowed'), mftSubaddons);
    }

    // Fallback: some payloads carry products as arrays/objects instead of flat noOf* keys.
    const productsNode = getByAnyKey('products');
    if (productRows.length === 0 && productsNode && typeof productsNode === 'object') {
        const categories = Object.entries(productsNode);
        categories.forEach(([categoryName, rawList]) => {
            const list = Array.isArray(rawList) ? rawList : rawList && typeof rawList === 'object' ? [rawList] : [];
            list.forEach((item) => {
                const addonName = item?.productName ?? item?.name ?? '—';
                const quantity = item?.quantity ?? item?.qty ?? 0;
                const addons = item?.addons;
                const subaddons = Array.isArray(addons)
                    ? addons.map((a) => formatLicensePayloadAddonLabel(a))
                    : addons && typeof addons === 'object'
                        ? Object.keys(addons).map((a) => formatLicensePayloadAddonLabel(a))
                        : [];
                const storageGb = Number(item?.storageGb ?? item?.sizeGb ?? 0);
                const isEntraItem =
                    String(addonName || '').trim() === 'microsoftEntra' ||
                    String(addonName || '').trim() === 'Microsoft Entra';
                if (!isEntraItem && Number.isFinite(storageGb) && storageGb > 0) {
                    subaddons.push(`Storage: ${storageGb} GB`);
                }
                pushProductRow(formatLabel(categoryName), formatLicensePayloadProductLabel(addonName), quantity, subaddons);
            });
        });
    }

    const renderValue = (value, key) => {
        if (value === null || value === undefined) return <span style={{ color: '#6b7280' }}>—</span>;
        if (typeof value === 'string' && value.trim().toLowerCase() === 'na') return null;
        if (Array.isArray(value)) return <span>{value.length ? value.join(', ') : 'None'}</span>;
        const yesNo = parseYesNoToken(value);
        if (yesNo === 'no') return null;
        if (yesNo === 'yes') return <Icon name="check" color="green" />;

        if (typeof value === 'object') {
            // License payload is typically flat, but just in case:
            let text = JSON.stringify(value, null, 2);
            const MAX = 20000;
            if (text.length > MAX) text = text.slice(0, MAX) + '\n... (truncated) ...';
            return (
                <pre
                    style={{
                        margin: '6px 0 0 0',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        color: '#0f172a'
                    }}
                >
                    {text}
                </pre>
            );
        }

        return <span>{formatDisplayValue(value, key)}</span>;
    };

    // Filter out OFF rows early so maxKeys behaves as expected.
    const filteredEntries = entries.filter(([key, value]) => {
        if (productRows.length > 0 && productRelatedKeys.has(key)) return false;
        if (typeof value === 'string' && value.trim().toLowerCase() === 'na') return false;
        return parseYesNoToken(value) !== 'no';
    });

    return (
        <div>
            {productRows.length > 0 && (
                <div style={{ marginBottom: '0.9rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {Object.entries(productRows.reduce((acc, row) => {
                        if (!acc[row.productName]) acc[row.productName] = [];
                        acc[row.productName].push(row);
                        return acc;
                    }, {})).map(([productName, rows]) => (
                        <div key={productName} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', background: '#fff' }}>
                            <div style={{ padding: '10px 14px', background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)', fontWeight: 600, color: '#334155', fontSize: '13px', borderBottom: '1px solid #e2e8f0' }}>
                                {productName}
                            </div>
                            <Table compact size="small" style={{ margin: 0 }}>
                                <Table.Header>
                                    <Table.Row>
                                        <Table.HeaderCell style={{ background: '#f8fafc', fontWeight: 600, color: '#475569' }}>Product Name</Table.HeaderCell>
                                        <Table.HeaderCell style={{ background: '#f8fafc', fontWeight: 600, color: '#475569' }}>Quantity</Table.HeaderCell>
                                        <Table.HeaderCell style={{ background: '#f8fafc', fontWeight: 600, color: '#475569' }}>Add-on</Table.HeaderCell>
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {rows.map((row, idx) => (
                                        <Table.Row key={`${productName}-${row.addonName}-${idx}`} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                            <Table.Cell style={{ color: '#1e293b' }}>{row.addonName}</Table.Cell>
                                            <Table.Cell style={{ color: '#475569' }}>{row.quantity}</Table.Cell>
                                            <Table.Cell style={{ color: '#64748b', fontSize: '13px' }}>
                                                {row.subaddons.length > 0 ? row.subaddons.join(', ') : 'None'}
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        </div>
                    ))}
                </div>
            )}
            <Table definition compact size="small" style={{ margin: 0 }}>
                <Table.Body>
                    {filteredEntries.slice(0, maxKeys).map(([key, value], idx) => (
                        <Table.Row key={key} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                            <Table.Cell
                                width={5}
                                style={{
                                    fontWeight: 600,
                                    color: '#475569',
                                    verticalAlign: 'top',
                                    padding: '10px 14px',
                                    borderColor: '#f1f5f9'
                                }}
                            >
                                {formatLabel(key)}
                            </Table.Cell>
                            <Table.Cell
                                style={{
                                    wordBreak: 'break-word',
                                    whiteSpace: 'pre-wrap',
                                    fontSize: '14px',
                                    color: '#334155',
                                    padding: '10px 14px',
                                    borderColor: '#f1f5f9'
                                }}
                            >
                                {renderValue(value, key)}
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
            {filteredEntries.length > maxKeys && (
                <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '8px' }}>Showing first {maxKeys} keys.</div>
            )}
        </div>
    );
}

// Format key for display (camelCase -> Title Case, etc.)
function formatLabel(key) {
    if (key === 'cloudSizeAllowed') {
        return 'Cloud Size Allowed (GB)';
    }
    if (key === 'noOfBackupUsersAllowed') {
        return 'Product endpoint';
    }
    if (key === 'noOfSyncUsersAllowed') {
        return 'Product MFT';
    }
    if (key === 'noOfMiniCloudsAllowed') {
        return 'Product MFT Add on MiniClouds';
    }
    if (key === 'noOfServerUsersAllowed') {
        return 'Server backup';
    }
    if (key === 'noOfEntraUsersAllowed' || key === 'noOfMicrosoftEntraUsersAllowed') {
        return 'Microsoft Entra';
    }
    // Server add-ons in the license file are booleans, not separate *Allowed counts
    if (key === 'ServerBackupEnabled') {
        return 'SQL Backup';
    }
    if (key === 'BackupNetworkDriveEnabled') {
        return 'Backup Network Drives';
    }
    return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (s) => s.toUpperCase())
        .replace(/_/g, ' ')
        .trim();
}

// Human-readable value: dates, booleans, 0/1 for *Enabled / isUnlimited
function formatDisplayValue(value, key = '') {
    if (value === null || value === undefined) return '—';
    if (Array.isArray(value)) return value.length === 0 ? 'None' : value.join(', ');
    const keyLower = (key || '').toLowerCase();
    const isBoolKey =
        keyLower.endsWith('enabled') ||
        keyLower === 'isunlimited' ||
        keyLower.startsWith('enable ') ||
        keyLower.includes(' enable ') ||
        keyLower.startsWith('is ');
    const isDateLikeKey =
        keyLower.includes('validity') ||
        keyLower.includes('expiry') ||
        keyLower.includes('expiration') ||
        keyLower.includes('creation') ||
        keyLower === 'date' ||
        keyLower.endsWith('date');

    const formatEpochMillis = (num) => {
        if (typeof num !== 'number' || !Number.isFinite(num)) return null;
        // Treat large integers as milliseconds since epoch (>= 10^11 ~ year 1973)
        if (num < 1e11) return null;
        const d = new Date(num);
        if (Number.isNaN(d.getTime())) return null;
        return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    };

    // Numbers like 1775260800000 / "1775260800000" for validity / creation dates
    if (isDateLikeKey) {
        if (typeof value === 'number') {
            const formatted = formatEpochMillis(value);
            if (formatted) return formatted;
        }
        if (typeof value === 'string' && /^\d{12,15}$/.test(value.trim())) {
            const asNumber = Number(value.trim());
            const formatted = formatEpochMillis(asNumber);
            if (formatted) return formatted;
        }
    }

    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(T|$)/.test(value.trim())) {
        try {
            const d = new Date(value);
            if (!Number.isNaN(d.getTime())) return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
        } catch (_) {}
    }
    if (value === true || value === 'true') return 'Yes';
    if (value === false || value === 'false') return 'No';
    if (isBoolKey && (value === 1 || value === '1')) return 'Yes';
    if (isBoolKey && (value === 0 || value === '0')) return 'No';
    return String(value);
}

// Map API payload ids to readable labels (license request products table)
function formatLicensePayloadProductLabel(raw) {
    if (raw == null || raw === '') return '—';
    const key = String(raw);
    const map = {
        serverBackup: 'Server backup',
        microsoftEntra: 'Microsoft Entra',
        endpointBackup: 'Endpoint backup',
        mft: 'Managed File Transfer (MFT / BluSync)',
        oneDrive: 'OneDrive',
        exchange: 'Exchange',
        sharePoint: 'SharePoint',
        gmail: 'Gmail',
        gDrive: 'GDrive'
    };
    if (map[key]) return map[key];
    return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (s) => s.toUpperCase())
        .trim();
}

function formatLicensePayloadAddonLabel(raw) {
    if (raw == null || raw === '') return '';
    const map = {
        sqlBackup: 'SQL Backup',
        backupNetworkDrive: 'Backup Network Drives',
        ransomwareDefenseSuite: 'Ransomware Defense Suite (RDS)',
        endpointRestoreAvScan: 'Anti-Malware Scanning',
        cloudToCloudRestore: 'Cloud to Cloud (C2C) Restore',
        miniCloudsPack: 'MiniClouds',
        avScanEnabled: 'Anti-Malware Scanning',
        eDiscoverySearch: 'eDiscovery / Search'
    };
    const key = String(raw);
    if (map[key]) return map[key];
    return formatLicensePayloadProductLabel(raw);
}

// User-friendly products summary (no raw JSON)
function ProductsReadable({ data, mode = 'default' }) {
    if (data == null || typeof data !== 'object') return <span style={{ color: '#6b7280' }}>—</span>;
    const categories = Object.entries(data);
    if (categories.length === 0) return <span style={{ color: '#6b7280' }}>None</span>;

    // Special layout for generated license: Product | Add-on | Sub add-on | Quantity
    if (mode === 'generated') {
        const generatedRows = Array.isArray(data.generated) ? data.generated : [];
        if (generatedRows.length === 0) {
            return <span style={{ color: '#6b7280' }}>None</span>;
        }

        // Group by product so the UI reads:
        // M365 Enterprise
        //   OneDrive add-on ...
        const byProduct = generatedRows.reduce((acc, row) => {
            const key = row.productName || '—';
            if (!acc[key]) acc[key] = [];
            acc[key].push(row);
            return acc;
        }, {});

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {Object.entries(byProduct).map(([productName, rowsForProduct]) => (
                    <div key={productName} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', background: '#fff' }}>
                        <div style={{ padding: '10px 14px', background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)', fontWeight: 600, color: '#334155', fontSize: '13px', borderBottom: '1px solid #e2e8f0' }}>
                            {productName}
                        </div>
                        <div style={{ padding: '12px 14px', overflowX: 'auto' }}>
                            <Table compact size="small" style={{ margin: 0, width: '100%', tableLayout: 'fixed', minWidth: '520px' }}>
                                <colgroup>
                                    <col style={{ width: '33%' }} />
                                    <col style={{ width: '90px' }} />
                                    <col style={{ width: 'calc(67% - 90px)' }} />
                                </colgroup>
                                <Table.Header>
                                    <Table.Row>
                                        <Table.HeaderCell style={{ background: '#f8fafc', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Product Name</Table.HeaderCell>
                                        <Table.HeaderCell style={{ background: '#f8fafc', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap', textAlign: 'center' }}>Quantity</Table.HeaderCell>
                                        <Table.HeaderCell style={{ background: '#f8fafc', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Add-on</Table.HeaderCell>
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {rowsForProduct.map((row, idx) => (
                                        <Table.Row key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                            <Table.Cell style={{ textTransform: 'capitalize', color: '#1e293b', wordBreak: 'break-word' }}>
                                                {row.addonName || '—'}
                                            </Table.Cell>
                                            <Table.Cell style={{ color: '#475569', whiteSpace: 'nowrap', textAlign: 'center' }}>
                                                {row.quantity}
                                            </Table.Cell>
                                            <Table.Cell style={{ color: '#64748b', fontSize: '13px', wordBreak: 'break-word' }}>
                                                {Array.isArray(row.subaddons) && row.subaddons.length > 0
                                                    ? row.subaddons.join(', ')
                                                    : 'None'}
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {categories.map(([key, list]) => {
                const categoryLabel = formatLabel(key);
                const items = Array.isArray(list)
                    ? list
                    : list != null && typeof list === 'object' && (list.productName != null || list.quantity != null)
                        ? [list]
                        : [];
                return (
                    <div key={key} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', background: '#fff' }}>
                        <div style={{ padding: '10px 14px', background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)', fontWeight: 600, color: '#334155', fontSize: '13px', borderBottom: '1px solid #e2e8f0' }}>
                            {categoryLabel}
                        </div>
                        <div style={{ padding: '12px 14px', overflowX: 'auto' }}>
                            {items.length === 0 ? (
                                <span style={{ color: '#64748b', fontSize: '13px' }}>No items</span>
                            ) : (
                                <Table compact size="small" style={{ margin: 0, width: '100%', tableLayout: 'fixed', minWidth: '520px' }}>
                                    <colgroup>
                                        <col style={{ width: '33%' }} />
                                        <col style={{ width: '90px' }} />
                                        <col style={{ width: 'calc(67% - 90px)' }} />
                                    </colgroup>
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.HeaderCell style={{ background: '#f8fafc', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Product</Table.HeaderCell>
                                            <Table.HeaderCell style={{ background: '#f8fafc', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap', textAlign: 'center' }}>Quantity</Table.HeaderCell>
                                            <Table.HeaderCell style={{ background: '#f8fafc', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Add-ons</Table.HeaderCell>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {items.map((item, idx) => {
                                            const rawName = item?.productName ?? item?.name ?? '';
                                            const name = formatLicensePayloadProductLabel(rawName);
                                            const qty = item?.quantity ?? '—';
                                            const addons = item?.addons;
                                            const addonList = Array.isArray(addons)
                                                ? addons
                                                : addons && typeof addons === 'object'
                                                    ? Object.keys(addons)
                                                    : [];
                                            const addonText =
                                                addonList.length > 0
                                                    ? addonList.map(formatLicensePayloadAddonLabel).join(', ')
                                                    : 'None';
                                            return (
                                                <Table.Row key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                                    <Table.Cell style={{ color: '#1e293b' }}>{name}</Table.Cell>
                                                    <Table.Cell style={{ color: '#475569', whiteSpace: 'nowrap', textAlign: 'center' }}>{qty}</Table.Cell>
                                                    <Table.Cell style={{ color: '#64748b', fontSize: '13px', wordBreak: 'break-word' }}>{addonText}</Table.Cell>
                                                </Table.Row>
                                            );
                                        })}
                                    </Table.Body>
                                </Table>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// User-friendly storage summary (no raw JSON)
function StorageReadable({ data }) {
    if (data == null || typeof data !== 'object') return <span style={{ color: '#6b7280' }}>—</span>;
    const type = data.type ?? data.storageType ?? '—';
    const sizeGb = data.sizeGb ?? data.gb ?? data.size ?? 0;
    const sizeLabel = typeof sizeGb === 'number' ? `${sizeGb} GB` : sizeGb;
    return (
        <div style={{ padding: '12px 14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'inline-block' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '14px', color: '#334155' }}>
                <div><strong style={{ color: '#475569', marginRight: '6px' }}>Type:</strong> <span style={{ textTransform: 'uppercase', fontWeight: 500 }}>{String(type)}</span></div>
                <div><strong style={{ color: '#475569', marginRight: '6px' }}>Size:</strong> {sizeLabel}</div>
            </div>
        </div>
    );
}

// Renders a flat or nested object as a definition table (key: value rows). Products and storage use readable components.
function DetailsSection({ title, icon, data, skipKeys = [], maxRows = 120, description }) {
    if (data == null || typeof data !== 'object') {
        return (
            <Segment style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
                <Header as="h5" style={{ marginTop: 0, color: '#0f172a', fontSize: '1.1rem' }}>
                    <Icon name={icon} style={{ color: '#3b82f6' }} /> {title}
                </Header>
                <p style={{ color: '#64748b', margin: 0 }}>No data</p>
            </Segment>
        );
    }
    const skipSet = new Set([
        ...skipKeys,
        '_id',
        // Hide internal / non-user-friendly fields globally
        'opportunityType',
        'opportunity_type',
        'isMultiTenancySetup',
        'is_multi_tenancy_setup',
        'smbFlow',
        'smb_flow',
        // Counts that are fully represented in the new products/add-ons design
        'noOfBackupUsersAllowed',
        'noOfSyncUsersAllowed',
        'noOfOdbBackupUsersAllowed',
        'noOfExchangeBackupUsersAllowed',
        'noOfSharepointBackupUsersAllowed',
        'noOfGmailBackupUsersAllowed',
        'noOfGdriveBackupUsersAllowed',
        'noOfServerUsersAllowed',
        'noOfEntraUsersAllowed',
        'noOfMicrosoftEntraUsersAllowed',
        'noOfMiniCloudsAllowed',
        'noOfUsersAllowed',
        'noOfDevicesAllowed',
        // Global flags / toggles we no longer want as separate rows
        'isUnlimited',
        'GmailBackupEnabled',
        'GdriveBackupEnabled',
        'OneDriveBackupEnabled',
        'ExchangeBackupEnabled',
        'SharepointBackupEnabled',
        'contentSearchEnabled',
        'GlobalSearchEnabled',
        'LoginType',
        // Endpoint / MFT capability flags now represented as product add-ons
        'BackupEnabled',
        'c2cRestoreEnabled',
        'RWLicenseAllowed',
        'isEndpointRestoreAVScanEnabled',
        'enableContentSearchForEndPoint',
        'SyncEnabled',
        'ClamAVLicenseAllowed',
        'EnableContentSearchForSync',
        'sqlLicenseAllowed',
        // Per-workload sub-addons that are rendered as "Sub add-ons" in the new design
        'EnableContentSearchForExchange',
        'EnableContentSearchForOnedrive',
        'EnableContentSearchForSharepoint',
        'EnableContentSearchForGmail',
        'EnableContentSearchForGdrive',
        'EnableContentSearchForEntra',
        'EnableContentSearchForMicrosoftEntra',
        'OneDriveArchivingEnabled',
        'MailArchivingEnabled',
        'SharepointArchivingEnabled',
        'InplaceArchiveEnabled',
        // Server-specific toggles that are shown as sub-addons under Server
        'ServerBackupEnabled',
        'BackupNetworkDriveEnabled'
    ]);
    const rows = [];

            function collect(obj, prefix = '') {
        if (rows.length >= maxRows) return;
        Object.entries(obj).forEach(([key, value]) => {
            if (skipSet.has(key)) return;
            const label = prefix ? `${formatLabel(prefix)} → ${formatLabel(key)}` : formatLabel(key);
            if (key === 'products' && value != null && typeof value === 'object') {
                const mode = title === 'Generated license (provisioned)' ? 'generated' : 'default';
                rows.push({ label, value: <ProductsReadable data={value} mode={mode} />, isNode: true });
                return;
            }
            if (key === 'storage' && value != null && typeof value === 'object') {
                rows.push({ label, value: <StorageReadable data={value} />, isNode: true });
                return;
            }
            if (Array.isArray(value)) {
                const hasObjects = value.some((v) => v != null && typeof v === 'object');
                rows.push({
                    label,
                    value: hasObjects ? JSON.stringify(value, null, 2) : formatDisplayValue(value, key),
                    sub: hasObjects,
                    isNode: false
                });
                return;
            }
            if (value != null && typeof value === 'object') {
                const hasNestedObjects = Object.values(value).some((v) => v != null && typeof v === 'object' && !Array.isArray(v));
                if (hasNestedObjects) {
                    rows.push({ label, value: JSON.stringify(value, null, 2), sub: true, isNode: false });
                } else {
                    collect(value, key);
                }
            } else {
                // For boolean-like flags, only show when effectively "enabled" (1 / true).
                const keyLower = (key || '').toLowerCase();
                const looksBooleanFlag =
                    keyLower.endsWith('enabled') ||
                    keyLower.startsWith('is') ||
                    keyLower.includes(' enabled ');
                if (looksBooleanFlag) {
                    const isOn =
                        value === true ||
                        value === 'true' ||
                        value === 'True' ||
                        value === 1 ||
                        value === '1';
                    if (!isOn) return;
                }

                const display = formatDisplayValue(value, key);
                rows.push({ label, value: display, sub: false, isNode: false });
            }
        });
    }
    collect(data);

    // Ensure important identifiers like Cloud Name appear first in the table
    if (rows.length > 1) {
        rows.sort((a, b) => {
            const aLabel = (a.label || '').toLowerCase();
            const bLabel = (b.label || '').toLowerCase();
            const aIsCloud = aLabel.includes('cloud') && aLabel.includes('name');
            const bIsCloud = bLabel.includes('cloud') && bLabel.includes('name');
            if (aIsCloud && !bIsCloud) return -1;
            if (bIsCloud && !aIsCloud) return 1;
            return 0;
        });
    }

    return (
        <Segment style={{ marginBottom: '1.5rem', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <Header as="h5" style={{ marginTop: 0, marginBottom: description ? '0.25rem' : '0.75rem', color: '#0f172a', fontSize: '1.1rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem' }}>
                <Icon name={icon} style={{ color: '#3b82f6' }} /> {title}
            </Header>
            {description && <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '0 0 0.75rem 0' }}>{description}</p>}
            <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                <Table definition compact style={{ marginTop: 0 }}>
                    <Table.Body>
                        {rows.slice(0, maxRows).map((r, idx) => (
                            <Table.Row key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                <Table.Cell width={5} style={{ fontWeight: 600, color: '#475569', verticalAlign: 'top', padding: '10px 14px', borderColor: '#f1f5f9' }}>
                                    {r.label}
                                </Table.Cell>
                                <Table.Cell style={{ wordBreak: 'break-word', whiteSpace: r.sub ? 'pre-wrap' : 'normal', fontFamily: r.sub ? 'monospace' : 'inherit', fontSize: r.sub ? '12px' : '14px', color: '#334155', padding: '10px 14px', borderColor: '#f1f5f9' }}>
                                    {r.isNode ? r.value : r.value}
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </div>
        </Segment>
    );
}

// Normalize license file JSON for display: keys with null or garbage prefix (e.g. "ÿÿ...\u0000cloudName") become clean keys like "cloudName" so values show correctly.
function normalizeLicenseFileForDisplay(obj) {
    if (obj == null || typeof obj !== 'object' || Array.isArray(obj)) return obj;
    const out = {};
    let cloudNameFromGarbageKey = null;
    Object.entries(obj).forEach(([key, value]) => {
        const trimmed = key.replace(/\0/g, '').trim();
        const isGarbageCloudName = (key.includes('\u0000') || key.includes('\0')) && (trimmed === 'cloudName' || key.endsWith('cloudName'));
        if (isGarbageCloudName) {
            cloudNameFromGarbageKey = value;
            return;
        }
        if (/^[\x00-\x1F\x7F\u00FF\s]+$/.test(key) || key.indexOf('\0') !== -1) {
            const clean = key.replace(/\0/g, '').replace(/^[\s\u00FF]+/g, '').trim();
            if (clean) out[clean] = value;
            return;
        }
        out[key] = value;
    });
    if (cloudNameFromGarbageKey !== null) out.cloudName = cloudNameFromGarbageKey;
    return out;
}

// Parse license text safely even when upstream prepends garbage bytes before JSON.
function parseLicenseFileText(text) {
    if (typeof text !== 'string') return null;
    const trimmed = text.trim();
    if (!trimmed) return null;

    const tryParse = (raw) => {
        try {
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === 'object' ? parsed : null;
        } catch {
            return null;
        }
    };

    const direct = tryParse(trimmed);
    if (direct) return normalizeLicenseFileForDisplay(direct);

    const firstBrace = trimmed.indexOf('{');
    const lastBrace = trimmed.lastIndexOf('}');
    if (firstBrace !== -1 && lastBrace > firstBrace) {
        const sliced = trimmed.slice(firstBrace, lastBrace + 1);
        const parsedSliced = tryParse(sliced);
        if (parsedSliced) return normalizeLicenseFileForDisplay(parsedSliced);
    }

    return null;
}

function normalizeCloudSizeAllowedValue(value) {
    if (value === null || value === undefined) return value;
    const normalized = typeof value === 'number' ? value : Number(String(value).trim());
    // No-storage license payloads can come back with this sentinel size instead of "Unlimited".
    if (Number.isFinite(normalized) && normalized >= 5242880000) {
        return 'Unlimited';
    }
    return value;
}

// Build a products-style view for generated license limits specialized for the flattened API payload.
function buildGeneratedLicenseDisplay(generatedLicense) {
    if (generatedLicense == null || typeof generatedLicense !== 'object') return generatedLicense;

    const boolOn = (key) => {
        const v = generatedLicense[key];
        return v === 1 || v === true || v === '1' || v === 'true';
    };

    const rows = [];

    // M365 Enterprise – OneDrive
    if (generatedLicense.noOfOdbBackupUsersAllowed != null) {
        if (Number(generatedLicense.noOfOdbBackupUsersAllowed) > 0) {
        const subaddons = [];
        if (boolOn('EnableContentSearchForOnedrive')) subaddons.push('eDiscovery / Search');
        if (boolOn('OneDriveArchivingEnabled')) subaddons.push('Capacity Savings');
        rows.push({
            productName: 'M365 Enterprise',
            addonName: 'OneDrive',
            quantity: String(generatedLicense.noOfOdbBackupUsersAllowed),
            subaddons
        });
        }
    }

    // M365 Enterprise – Exchange
    if (generatedLicense.noOfExchangeBackupUsersAllowed != null) {
        if (Number(generatedLicense.noOfExchangeBackupUsersAllowed) > 0) {
        const subaddons = [];
        if (boolOn('EnableContentSearchForExchange')) subaddons.push('eDiscovery / Search');
        if (boolOn('MailArchivingEnabled')) subaddons.push('Capacity Savings');
        if (boolOn('InplaceArchiveEnabled')) subaddons.push('In-Place Archive');
        rows.push({
            productName: 'M365 Enterprise',
            addonName: 'Exchange',
            quantity: String(generatedLicense.noOfExchangeBackupUsersAllowed),
            subaddons
        });
        }
    }

    // M365 Enterprise – SharePoint
    if (generatedLicense.noOfSharepointBackupUsersAllowed != null) {
        if (Number(generatedLicense.noOfSharepointBackupUsersAllowed) > 0) {
        const subaddons = [];
        if (boolOn('EnableContentSearchForSharepoint')) subaddons.push('eDiscovery / Search');
        if (boolOn('SharepointArchivingEnabled')) subaddons.push('Capacity Savings');
        rows.push({
            productName: 'M365 Enterprise',
            addonName: 'SharePoint',
            quantity: String(generatedLicense.noOfSharepointBackupUsersAllowed),
            subaddons
        });
        }
    }

    // Google Workspace – Gmail
    if (generatedLicense.noOfGmailBackupUsersAllowed != null) {
        if (Number(generatedLicense.noOfGmailBackupUsersAllowed) > 0) {
        const subaddons = [];
        if (boolOn('EnableContentSearchForGmail')) subaddons.push('eDiscovery / Search');
        rows.push({
            productName: 'Google Workspace',
            addonName: 'Gmail',
            quantity: String(generatedLicense.noOfGmailBackupUsersAllowed),
            subaddons
        });
        }
    }

    // Google Workspace – GDrive
    if (generatedLicense.noOfGdriveBackupUsersAllowed != null) {
        if (Number(generatedLicense.noOfGdriveBackupUsersAllowed) > 0) {
        const subaddons = [];
        if (boolOn('EnableContentSearchForGdrive')) subaddons.push('eDiscovery / Search');
        rows.push({
            productName: 'Google Workspace',
            addonName: 'GDrive',
            quantity: String(generatedLicense.noOfGdriveBackupUsersAllowed),
            subaddons
        });
        }
    }

    // Server: quantity is only noOfServerUsersAllowed. SQL Backup / Backup Network Drives
    // appear as add-ons when ServerBackupEnabled / BackupNetworkDriveEnabled are on (not separate *Allowed keys).
    if (generatedLicense.noOfServerUsersAllowed != null) {
        if (Number(generatedLicense.noOfServerUsersAllowed) > 0) {
        const subaddons = [];
        if (boolOn('ServerBackupEnabled')) subaddons.push('SQL Backup');
        if (boolOn('BackupNetworkDriveEnabled')) subaddons.push('Backup Network Drives');
        rows.push({
            productName: 'Server',
            addonName: 'Server backup',
            quantity: String(generatedLicense.noOfServerUsersAllowed),
            subaddons
        });
        }
    }

    // Microsoft Entra (shown as its own product in UI; provisioning still uses m365Enterprise)
    const entraQty =
        generatedLicense.noOfEntraUsersAllowed ?? generatedLicense.noOfMicrosoftEntraUsersAllowed;
    if (entraQty != null && Number(entraQty) > 0) {
        const entraSubaddons = [];
        if (boolOn('EnableContentSearchForEntra') || boolOn('EnableContentSearchForMicrosoftEntra')) {
            entraSubaddons.push('eDiscovery / Search');
        }
        rows.push({
            productName: 'Microsoft Entra',
            addonName: 'Microsoft Entra',
            quantity: String(entraQty),
            subaddons: entraSubaddons
        });
    }

    // Endpoint
    if (generatedLicense.noOfBackupUsersAllowed != null) {
        if (Number(generatedLicense.noOfBackupUsersAllowed) > 0) {
            const endpointSubaddons = [];
            if (boolOn('c2cRestoreEnabled')) endpointSubaddons.push('Cloud to Cloud (C2C) Restore');
            if (boolOn('RWLicenseAllowed')) endpointSubaddons.push('Ransomware Defense Suite (RDS)');
            if (boolOn('isEndpointRestoreAVScanEnabled')) endpointSubaddons.push('Anti-Malware Scanning');
            if (boolOn('enableContentSearchForEndPoint')) {
                endpointSubaddons.push('eDiscovery / Search');
            }
            rows.push({
                productName: 'Endpoint',
                addonName: 'Endpoint backup',
                quantity: String(generatedLicense.noOfBackupUsersAllowed),
                subaddons: endpointSubaddons
            });
        }
    }

    // Managed File Transfer (MFT / BluSync)
    if (generatedLicense.noOfSyncUsersAllowed != null) {
        if (Number(generatedLicense.noOfSyncUsersAllowed) > 0) {
            const mftSubaddons = [];
            if (generatedLicense.noOfMiniCloudsAllowed != null && Number(generatedLicense.noOfMiniCloudsAllowed) > 0) {
                mftSubaddons.push(`MiniClouds (Qty:${String(generatedLicense.noOfMiniCloudsAllowed)})`);
            }
            if (boolOn('ClamAVLicenseAllowed')) mftSubaddons.push('Anti-Malware Scanning');
            if (boolOn('EnableContentSearchForSync')) mftSubaddons.push('eDiscovery / Search');
            rows.push({
                productName: 'Managed File Transfer (MFT / BluSync)',
                addonName: 'MFT (Managed File Transfer)',
                quantity: String(generatedLicense.noOfSyncUsersAllowed),
                subaddons: mftSubaddons
            });
        }
    }

    if (rows.length === 0) return generatedLicense;

    // Keep formatted view focused: only show core summary fields + products table.
    return {
        cloudName: generatedLicense.cloudName,
        validityPeriod: generatedLicense.validityPeriod,
        creationDate: generatedLicense.creationDate,
        cloudSizeAllowed: normalizeCloudSizeAllowedValue(generatedLicense.cloudSizeAllowed),
        products: {
            generated: rows
        }
    };
}

// Dedicated View details layout: License request (source) + Generated license (provisioned). Raw data is separate.
function LicenseRequestDetailsDesign({ data }) {
    const licenseRequest = data?.licenseRequest ?? data?.request ?? null;
    const generatedLicenseRaw = data?.generatedLicense ?? data?.generated ?? null;
    const storageTypeRaw =
        licenseRequest?.storage?.type ??
        generatedLicenseRaw?.storage?.type ??
        generatedLicenseRaw?.storageType ??
        generatedLicenseRaw?.type ??
        null;
    const isByosStorage = typeof storageTypeRaw === 'string' && storageTypeRaw.trim().toLowerCase() === 'byos';
    const generatedLicense = (() => {
        const baseDisplay = buildGeneratedLicenseDisplay(generatedLicenseRaw);
        if (!isByosStorage || baseDisplay == null || typeof baseDisplay !== 'object') {
            return baseDisplay;
        }
        return {
            ...baseDisplay,
            cloudSizeAllowed: 'Unlimited'
        };
    })();
    const hasStructured = licenseRequest != null || generatedLicense != null;

    if (!hasStructured) {
        return <FormattedDetailsView data={data} />;
    }

    const cloudName = licenseRequest?.cloudName ?? generatedLicense?.cloudName ?? '—';

    return (
        <div style={{ padding: '0.25rem 0' }}>
            {cloudName !== '—' && (
                <div style={{ marginBottom: '1.25rem', padding: '14px 16px', background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', borderRadius: '10px', border: '1px solid #bfdbfe' }}>
                    <span style={{ fontSize: '12px', color: '#1e40af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Cloud</span>
                    <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: 500, marginTop: '2px' }}>{cloudName}</div>
                </div>
            )}
            <DetailsSection
                title="Generated license (provisioned)"
                icon="key"
                data={generatedLicense}
                skipKeys={['_id', 'timestamp', 'requestId', 'request_id']}
                description="Provisioned limits and feature flags from the license server."
            />
        </div>
    );
}

const LicenseGenerator = ({ userEmail, userName, userRoles = [] }) => {
    const ADD_NEW_CLOUD_PREFIX = '__add_new_cloud__::';
    const { options: licenseProductOptions } = useDropdownOptions('license_products');

    const productCatalog = useMemo(
        () => buildProductCatalogFromOptions(licenseProductOptions),
        [licenseProductOptions]
    );

    const [customerName, setCustomerName] = useState(''); // selected or manually typed cloudName
    const [opportunityType, setOpportunityType] = useState('New');
    const [validityDate, setValidityDate] = useState('');
    const [error, setError] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [productSelection, setProductSelection] = useState(() =>
        buildInitialProductSelection(buildProductCatalogFromOptions([]))
    );
    const [storageType, setStorageType] = useState('');
    const [storageGb, setStorageGb] = useState('');
    const [storageAddons, setStorageAddons] = useState({
        crashPlan: true,
        microsoft: false
    });
    const [logModalOpen, setLogModalOpen] = useState(false);
    const [logLines, setLogLines] = useState([]);
    const [processedLogLines, setProcessedLogLines] = useState([]); // with timestamps, same pattern as MT form
    const [logStatus, setLogStatus] = useState('idle'); // 'idle' | 'running' | 'success' | 'error'
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const logStartTimeRef = useRef(null);
    const logTimerRef = useRef(null);
    const logContainerRef = useRef(null);
    const [cloudOptions, setCloudOptions] = useState(() => readCloudDirectoryCache().options);
    const [cloudMetaByName, setCloudMetaByName] = useState(() => readCloudDirectoryCache().meta);
    const [selectedCloudMeta, setSelectedCloudMeta] = useState(null);
    const [isLoadingClouds, setIsLoadingClouds] = useState(false);
    const [cloudQuery, setCloudQuery] = useState('');
    const [cloudFieldError, setCloudFieldError] = useState('');
    const cloudSearchDebounceRef = useRef(null);
    const [activeTab, setActiveTab] = useState('generator');
    const [historyList, setHistoryList] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState(null);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [detailsRequestId, setDetailsRequestId] = useState(null);
    const [detailsData, setDetailsData] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState(null);
    const [detailsViewRaw, setDetailsViewRaw] = useState(false);
    const [detailsRawCopied, setDetailsRawCopied] = useState(false);
    const [licenseDetailsModalOpen, setLicenseDetailsModalOpen] = useState(false);
    const [licenseDetailsCloudName, setLicenseDetailsCloudName] = useState(null);
    const [licenseDetailsCloudCopied, setLicenseDetailsCloudCopied] = useState(false);
    const [licenseDetailsData, setLicenseDetailsData] = useState(null);
    const [licenseDetailsLoading, setLicenseDetailsLoading] = useState(false);
    const [licenseDetailsError, setLicenseDetailsError] = useState(null);
    const [licenseDetailsDownloadingFileName, setLicenseDetailsDownloadingFileName] = useState(null);
    const [licenseDetailsCloudSizeAllowed, setLicenseDetailsCloudSizeAllowed] = useState(null);
    const [fileViewerOpen, setFileViewerOpen] = useState(false);
    const [fileViewerFileName, setFileViewerFileName] = useState(null);
    const [fileViewerHideHeaderFileName, setFileViewerHideHeaderFileName] = useState(false);
    const [fileViewerForceByosUnlimited, setFileViewerForceByosUnlimited] = useState(false);
    const [fileViewerBlobUrl, setFileViewerBlobUrl] = useState(null);
    const [fileViewerText, setFileViewerText] = useState(null);
    const [fileViewerMode, setFileViewerMode] = useState('readable'); // 'readable' | 'raw' (same as detailsViewRaw semantics: raw = show raw)
    const [fileViewerRawCopied, setFileViewerRawCopied] = useState(false);
    const [fileViewerLoading, setFileViewerLoading] = useState(false);
    const [fileViewerError, setFileViewerError] = useState(null);
    const [confirmOldDownloadOpen, setConfirmOldDownloadOpen] = useState(false);
    const [pendingOldDownload, setPendingOldDownload] = useState(null); // { cloudName, fileName }
    const [historySearchCloud, setHistorySearchCloud] = useState('');
    const [historySearchResults, setHistorySearchResults] = useState(null);
    const [historySearchLoading, setHistorySearchLoading] = useState(false);
    const [historySearchError, setHistorySearchError] = useState(null);
    const { rowsPerPage, activePage, setActivePage } = useTablePreferences();
    const cloudOptionsWithAddAction = useMemo(() => {
        const query = (cloudQuery || '').toString().trim();
        if (!query) return cloudOptions;

        const exists = cloudOptions.some(
            (opt) => (opt?.value || '').toString().trim().toLowerCase() === query.toLowerCase()
        );
        if (exists) return cloudOptions;

        return [
            {
                key: query,
                value: query,
                text: query
            },
            {
                key: `${ADD_NEW_CLOUD_PREFIX}${query}`,
                value: `${ADD_NEW_CLOUD_PREFIX}${query}`,
                text: `Add "${query}" as a new cloud name`,
                content: (
                    <span>
                        <Icon name="plus circle" style={{ marginRight: '0.45rem' }} />
                        <span style={{ color: '#6b7280' }}>Add </span>
                        {`"${query}" `}
                        <span style={{ color: '#6b7280' }}>as a new cloud name</span>
                    </span>
                )
            },
            ...cloudOptions
        ];
    }, [cloudOptions, cloudQuery]);

    // Rebuild selection shape whenever the configured product catalog changes
    useEffect(() => {
        setProductSelection(buildInitialProductSelection(productCatalog));
    }, [productCatalog]);

    const resetForm = () => {
        setCustomerName('');
        setOpportunityType('New');
        setValidityDate('');
        setError(null);
        setSubmitted(false);
        setProductSelection(buildInitialProductSelection(productCatalog));
        setStorageType('');
        setStorageGb('');
        setStorageAddons({
            crashPlan: true,
            microsoft: false
        });
        setCloudOptions([]);
        setCloudMetaByName({});
        setSelectedCloudMeta(null);
        setIsLoadingClouds(false);
        setCloudQuery('');
        setCloudFieldError('');
    };

    const loadCloudOptions = async (query = '') => {
        // Backend cloud search can fail on empty query; keep a safe bootstrap term.
        const normalizedQuery = (query || '').toString().trim();
        const effectiveQuery = normalizedQuery || 'a';
        setIsLoadingClouds(true);
        try {
            const response = await api.clouds.search(effectiveQuery);
            const clouds = Array.isArray(response?.data?.clouds) ? response.data.clouds : [];

            const unique = new Map();
            clouds.forEach((c) => {
                const cloudName = (c?.cloudName || '').toString().trim();
                if (!cloudName) return;
                if (!unique.has(cloudName)) {
                    unique.set(cloudName, c);
                }
            });

            const options = Array.from(unique.entries())
                .map(([cloudName, cloud]) => {
                    const customerDisplay = (cloud?.customerName || '').toString().trim();
                    return {
                        key: cloudName,
                        value: cloudName,
                        text: customerDisplay && customerDisplay !== cloudName
                            ? `${cloudName} (${customerDisplay})`
                            : cloudName
                    };
                })
                .sort((a, b) => a.text.localeCompare(b.text));

            setCloudOptions(options);
            const meta = {};
            unique.forEach((cloud, name) => {
                meta[name] = {
                    cloudName: (cloud?.cloudName || '').toString().trim() || name,
                    customerName: (cloud?.customerName || '').toString().trim() || null,
                    countryCode: (cloud?.countryCode || '').toString().trim() || null,
                    profile: (cloud?.profile || '').toString().trim() || null
                };
            });
            setCloudMetaByName((prev) => ({
                ...prev,
                ...meta
            }));
            writeCloudDirectoryCache(options, {
                ...cloudMetaByName,
                ...meta
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to load cloud options:', e);
            setCloudOptions([]);
        } finally {
            setIsLoadingClouds(false);
        }
    };

    // Preload cloud list on first render
    useEffect(() => {
        return () => {
            if (cloudSearchDebounceRef.current) {
                clearTimeout(cloudSearchDebounceRef.current);
                cloudSearchDebounceRef.current = null;
            }
        };
    }, []);

    // Fetch license details when History tab is active
    useEffect(() => {
        if (activeTab !== 'history') return;
        setHistoryError(null);
        setHistoryLoading(true);
        api.licenseGenerator
            .getAll()
            .then((res) => {
                const raw = res?.data?.data;
                setHistoryList(Array.isArray(raw) ? raw : []);
                setActivePage(1);
            })
            .catch((err) => {
                setHistoryList([]);
                setHistoryError(
                    err?.response?.data?.message || err?.message || 'Failed to load license details'
                );
            })
            .finally(() => setHistoryLoading(false));
    }, [activeTab]);

    // Reset to page 1 when switching to History tab
    useEffect(() => {
        if (activeTab === 'history') setActivePage(1);
    }, [activeTab, setActivePage]);

    // Helpers to normalize cloud name and request ID from history row (camelCase or snake_case)
    const getHistoryCloudName = (row) => {
        if (row == null || typeof row !== 'object') return '';
        return (row.cloudName ?? row.cloud_name ?? row.cloud ?? row.name ?? '').toString().trim();
    };
    const getHistoryRequestId = (row) => {
        if (row == null || typeof row !== 'object') return '';
        return (
            row.requestId ??
            row.request_id ??
            row.id ??
            row.licenseId ??
            row.license_id ??
            row.request?.requestId ??
            row.request?.request_id ??
            row.request?.id ??
            ''
        )
            .toString()
            .trim();
    };
    const formatHistoryDate = (d) => {
        if (!d || Number.isNaN(d.getTime())) return '';
        const day = String(d.getDate()).padStart(2, '0');
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const mmm = months[d.getMonth()];
        const yyyy = d.getFullYear();
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        const ss = String(d.getSeconds()).padStart(2, '0');
        return `${day}-${mmm}-${yyyy} ${hh}:${mm}:${ss}`;
    };
    const getHistoryDate = (row) => {
        if (row == null || typeof row !== 'object') return '';
        const idObj = row._id && typeof row._id === 'object' ? row._id : null;
        const raw =
            row.date ??
            row.createdAt ??
            row.created_at ??
            row.timestamp ??
            row.lastModified ??
            (idObj ? idObj.date ?? idObj.timestamp : undefined) ??
            row.requestedAt ??
            row.requested_at ??
            row.request?.date ??
            row.request?.createdAt ??
            row.request?.created_at ??
            row.request?.timestamp ??
            '';
        if (raw === '' || raw === undefined || raw === null) return '';
        let d;
        if (raw instanceof Date) {
            d = raw;
        } else if (typeof raw === 'number') {
            d = raw > 1e12 ? new Date(raw) : new Date(raw * 1000);
        } else {
            d = new Date(String(raw));
        }
        if (Number.isNaN(d.getTime())) return String(raw);
        return formatHistoryDate(d);
    };
    const getHistoryTimestamp = (row) => {
        if (row == null || typeof row !== 'object') return 0;
        const idObj = row._id && typeof row._id === 'object' ? row._id : null;
        const raw =
            row.date ??
            row.createdAt ??
            row.created_at ??
            row.timestamp ??
            row.lastModified ??
            (idObj ? idObj.date ?? idObj.timestamp : undefined) ??
            row.requestedAt ??
            row.requested_at ??
            row.request?.date ??
            row.request?.createdAt ??
            row.request?.created_at ??
            row.request?.timestamp ??
            null;
        if (raw == null || raw === '') return 0;
        if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? 0 : raw.getTime();
        if (typeof raw === 'number') return raw > 1e12 ? raw : raw * 1000;
        const parsed = new Date(String(raw));
        return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
    };
    const pickLatestHistoryRowByCloud = (rows, cloudName) => {
        const safeCloudName = (cloudName ?? '').toString().trim().toLowerCase();
        if (!safeCloudName) return null;
        const list = Array.isArray(rows) ? rows : [];
        const matches = list.filter((r) => getHistoryCloudName(r).toLowerCase() === safeCloudName);
        if (!matches.length) return null;
        return matches.sort((a, b) => getHistoryTimestamp(b) - getHistoryTimestamp(a))[0];
    };
    const getHistoryOpportunityType = (row) => {
        if (row == null || typeof row !== 'object') return '';
        const v = row.opportunityType ?? row.opportunity_type ?? row.request?.opportunityType ?? row.request?.opportunity_type ?? '';
        if (v == null || typeof v === 'object') return '';
        return String(v).trim();
    };

    const openDetailsModal = (row) => {
        const requestId = getHistoryRequestId(row);
        if (!requestId) return;
        setDetailsRequestId(requestId);
        setDetailsModalOpen(true);
        setDetailsData(null);
        setDetailsError(null);
        setDetailsViewRaw(false);
        setDetailsLoading(true);
        api.licenseGenerator
            .getRequest(requestId)
            .then((res) => setDetailsData(res?.data?.data ?? res?.data))
            .catch((err) => {
                setDetailsError(
                    err?.response?.data?.message || err?.message || 'Failed to load request details'
                );
            })
            .finally(() => setDetailsLoading(false));
    };

    const closeDetailsModal = () => {
        setDetailsModalOpen(false);
        setDetailsRequestId(null);
        setDetailsData(null);
        setDetailsError(null);
        setDetailsViewRaw(false);
    };

    const openLicenseDetailsModal = (row) => {
        const cloudName = getHistoryCloudName(row);
        const requestId = getHistoryRequestId(row);
        if (!cloudName) return;
        setLicenseDetailsCloudName(cloudName);
        setLicenseDetailsCloudCopied(false);
        setLicenseDetailsModalOpen(true);
        setLicenseDetailsData(null);
        setLicenseDetailsError(null);
        setLicenseDetailsLoading(true);
        setLicenseDetailsCloudSizeAllowed(null);
        api.licenseGenerator
            .getLicenseDetailsByCloudName(cloudName)
            .then((res) => setLicenseDetailsData(res?.data?.data ?? res?.data))
            .catch((err) => {
                setLicenseDetailsError(
                    err?.response?.data?.message || err?.message || 'Failed to load license details'
                );
            })
            .finally(() => setLicenseDetailsLoading(false));

        if (requestId) {
            api.licenseGenerator
                .getRequest(requestId)
                .then((res) => {
                    const detailsData = res?.data?.data ?? res?.data;
                    const licenseRequest = detailsData?.licenseRequest ?? detailsData?.request ?? null;
                    const generatedLicenseRaw = detailsData?.generatedLicense ?? detailsData?.generated ?? null;
                    const storageTypeRaw =
                        licenseRequest?.storage?.type ??
                        generatedLicenseRaw?.storage?.type ??
                        generatedLicenseRaw?.storageType ??
                        generatedLicenseRaw?.type ??
                        null;
                    const isByosStorage =
                        typeof storageTypeRaw === 'string' &&
                        storageTypeRaw.trim().toLowerCase() === 'byos';
                    const baseDisplay = buildGeneratedLicenseDisplay(generatedLicenseRaw);
                    const cloudSizeValue =
                        isByosStorage
                            ? 'Unlimited'
                            : (baseDisplay?.cloudSizeAllowed ?? generatedLicenseRaw?.cloudSizeAllowed ?? null);
                    setLicenseDetailsCloudSizeAllowed(cloudSizeValue);
                })
                .catch(() => {
                    setLicenseDetailsCloudSizeAllowed(null);
                });
        }
    };
    const openLicenseDetailsFromCloudName = async (cloudName) => {
        const safeCloudName = (cloudName ?? '').toString().trim();
        if (!safeCloudName) return;
        setLicenseDetailsError(null);
        setLicenseDetailsDownloadingFileName('__view__');
        try {
            const res = await api.licenseGenerator.getLicenseDetailsByCloudName(safeCloudName);
            const data = res?.data?.data ?? res?.data;
            const rawFiles =
                data?.files ??
                data?.request?.files ??
                (Array.isArray(data) ? data : []);

            const getFileDateRaw = (obj) => {
                if (obj == null || typeof obj !== 'object') return null;
                const raw =
                    obj.lastModified ??
                    obj.last_modified ??
                    obj.date ??
                    obj.createdAt ??
                    obj.created_at ??
                    obj.timestamp ??
                    obj.uploadedAt ??
                    obj.uploaded_at ??
                    '';
                if (!raw) return null;
                const d =
                    typeof raw === 'string'
                        ? new Date(raw)
                        : raw instanceof Date
                        ? raw
                        : typeof raw === 'number' && raw < 1e12
                        ? new Date(raw * 1000)
                        : new Date(raw);
                return Number.isNaN(d.getTime()) ? null : d;
            };

            const filesWithMeta = (Array.isArray(rawFiles) ? rawFiles : [])
                .map((f) => {
                    const fileName =
                        (f?.fileName ??
                            f?.name ??
                            f?.filename ??
                            (typeof f === 'string' ? f : '')).toString().trim();
                    const fileObj = typeof f === 'object' && f !== null ? f : {};
                    const fileTime = getFileDateRaw(fileObj)
                        ? getFileDateRaw(fileObj).getTime()
                        : 0;
                    return { fileName, fileTime };
                })
                .filter((f) => f.fileName);

            if (!filesWithMeta.length) {
                setLicenseDetailsError('No license files available to view for this cloud.');
                return;
            }

            const maxTime = Math.max(...filesWithMeta.map((f) => f.fileTime));
            const latest =
                filesWithMeta.find((f) => f.fileTime > 0 && f.fileTime === maxTime) ||
                filesWithMeta[0];

            openLicenseFileView(safeCloudName, latest.fileName, {
                hideHeaderFileName: true,
                forceByosUnlimited: storageType === 'BYOS'
            });
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                'Failed to fetch latest license details.';
            setLicenseDetailsError(typeof msg === 'string' ? msg : 'Failed to fetch latest license details.');
        } finally {
            setLicenseDetailsDownloadingFileName(null);
        }
    };

    const closeLicenseDetailsModal = () => {
        setLicenseDetailsModalOpen(false);
        setLicenseDetailsCloudName(null);
        setLicenseDetailsData(null);
        setLicenseDetailsError(null);
        setLicenseDetailsDownloadingFileName(null);
        setLicenseDetailsCloudCopied(false);
        setLicenseDetailsCloudSizeAllowed(null);
        closeLicenseFileView();
    };

    const openLicenseFileView = (cloudName, fileName, options = {}) => {
        if (!cloudName || !fileName) return;
        const { hideHeaderFileName = false, forceByosUnlimited = false } = options || {};
        setFileViewerOpen(true);
        setFileViewerFileName(fileName);
        setFileViewerHideHeaderFileName(!!hideHeaderFileName);
        setFileViewerForceByosUnlimited(!!forceByosUnlimited);
        setFileViewerBlobUrl(null);
        setFileViewerText(null);
        setFileViewerMode('readable');
        setFileViewerError(null);
        setFileViewerLoading(true);
        api.licenseGenerator
            .getLicenseFileViewBlob(cloudName, fileName)
            .then((res) => {
                const blob = res?.data instanceof Blob ? res.data : new Blob([res?.data ?? '']);
                const url = URL.createObjectURL(blob);
                setFileViewerBlobUrl(url);
                const type = (blob.type || '').toLowerCase();
                if (!type.includes('pdf')) {
                    blob.text().then((text) => setFileViewerText(text)).catch(() => setFileViewerText(null));
                }
            })
            .catch((err) => {
                const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to load file';
                setFileViewerError(typeof msg === 'string' ? msg : 'Failed to load file');
            })
            .finally(() => setFileViewerLoading(false));
    };

    const closeLicenseFileView = () => {
        if (fileViewerBlobUrl) {
            URL.revokeObjectURL(fileViewerBlobUrl);
        }
        setFileViewerOpen(false);
        setFileViewerFileName(null);
        setFileViewerHideHeaderFileName(false);
        setFileViewerForceByosUnlimited(false);
        setFileViewerBlobUrl(null);
        setFileViewerText(null);
        setFileViewerMode('readable');
        setFileViewerRawCopied(false);
        setFileViewerError(null);
    };

    const handleLicenseFileDownload = async (cloudName, fileName) => {
        setLicenseDetailsDownloadingFileName(fileName);
        setLicenseDetailsError(null);
        try {
            // Fetch file as blob so errors stay in-app (no new tab)
            const res = await api.licenseGenerator.downloadLicenseFileBlob(cloudName, fileName);
            const blob = res.data;
            const contentType = res.headers && (res.headers['content-type'] || res.headers['Content-Type'] || '');

            // If upstream still returns JSON (e.g. token or error), show a short message instead of navigating
            if (contentType && contentType.includes('application/json')) {
                const text = await blob.text().catch(() => '');
                let msg = 'Download failed';
                if (text) {
                    try {
                        const parsed = JSON.parse(text);
                        if (parsed && parsed.success && parsed.downloadToken) {
                            msg = 'Download not yet available from license API (received token instead of file). Please try again later.';
                        } else {
                            msg = parsed.message || parsed.error || text;
                        }
                    } catch (_) {
                        msg = text;
                    }
                }
                setLicenseDetailsError(msg);
                return;
            }

            // Otherwise, treat as a real file and trigger browser download via blob URL
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                'Download failed';
            setLicenseDetailsError(typeof msg === 'string' ? msg : 'Download failed');
        } finally {
            setLicenseDetailsDownloadingFileName(null);
        }
    };

    const downloadLatestLicenseForCloud = async (cloudName) => {
        if (!cloudName) return;
        setLicenseDetailsError(null);
        setLicenseDetailsDownloadingFileName('__latest__');
        try {
            const res = await api.licenseGenerator.getLicenseDetailsByCloudName(cloudName);
            const data = res?.data?.data ?? res?.data;
            const rawFiles =
                data?.files ??
                data?.request?.files ??
                (Array.isArray(data) ? data : []);

            const getFileDateRaw = (obj) => {
                if (obj == null || typeof obj !== 'object') return null;
                const raw =
                    obj.lastModified ??
                    obj.last_modified ??
                    obj.date ??
                    obj.createdAt ??
                    obj.created_at ??
                    obj.timestamp ??
                    obj.uploadedAt ??
                    obj.uploaded_at ??
                    '';
                if (!raw) return null;
                const d =
                    typeof raw === 'string'
                        ? new Date(raw)
                        : raw instanceof Date
                        ? raw
                        : typeof raw === 'number' && raw < 1e12
                        ? new Date(raw * 1000)
                        : new Date(raw);
                return Number.isNaN(d.getTime()) ? null : d;
            };

            const filesWithMeta = (Array.isArray(rawFiles) ? rawFiles : [])
                .map((f) => {
                    const fileName =
                        (f?.fileName ??
                            f?.name ??
                            f?.filename ??
                            (typeof f === 'string' ? f : '')).toString().trim();
                    const fileObj =
                        typeof f === 'object' && f !== null ? f : {};
                    const fileTime = getFileDateRaw(fileObj)
                        ? getFileDateRaw(fileObj).getTime()
                        : 0;
                    return { fileName, fileTime };
                })
                .filter((f) => f.fileName);

            if (!filesWithMeta.length) {
                setLicenseDetailsError(
                    'No license files available for download for this cloud.'
                );
                return;
            }

            const maxTime = Math.max(...filesWithMeta.map((f) => f.fileTime));
            const latest =
                filesWithMeta.find(
                    (f) => f.fileTime > 0 && f.fileTime === maxTime
                ) || filesWithMeta[0];

            await handleLicenseFileDownload(cloudName, latest.fileName);
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                'Failed to fetch latest license details.';
            setLicenseDetailsError(
                typeof msg === 'string'
                    ? msg
                    : 'Failed to fetch latest license details.'
            );
        } finally {
            setLicenseDetailsDownloadingFileName(null);
        }
    };

    const requestOldLicenseDownload = (cloudName, fileName) => {
        if (!cloudName || !fileName) return;
        setPendingOldDownload({ cloudName, fileName });
        setConfirmOldDownloadOpen(true);
    };

    const handleConfirmOldDownload = (proceed) => {
        if (!proceed) {
            setConfirmOldDownloadOpen(false);
            setPendingOldDownload(null);
            return;
        }
        if (pendingOldDownload) {
            const { cloudName, fileName } = pendingOldDownload;
            setConfirmOldDownloadOpen(false);
            setPendingOldDownload(null);
            handleLicenseFileDownload(cloudName, fileName);
        }
    };

    // Search by cloud name (same pattern as Expiring Licenses: Search button + Clear)
    const handleHistorySearch = () => {
        const term = (historySearchCloud || '').toString().trim();
        if (!term) {
            setHistorySearchResults(null);
            setHistorySearchError(null);
            setActivePage(1);
            return;
        }
        setHistorySearchError(null);
        setHistorySearchLoading(true);
        setHistorySearchResults([]); // avoid showing full list while request in flight
        api.licenseGenerator
            .getCloudNames(term)
            .then((res) => {
                const raw = res?.data?.data;
                setHistorySearchResults(Array.isArray(raw) ? raw : []);
                setActivePage(1);
            })
            .catch((err) => {
                setHistorySearchResults(null);
                setHistorySearchError(
                    err?.response?.data?.message || err?.message || 'Search failed'
                );
            })
            .finally(() => setHistorySearchLoading(false));
    };

    const scheduleReset = () => {
        setTimeout(() => {
            resetForm();
        }, 3000);
    };

    const appendLogLine = (line) => {
        setLogLines((prev) => [...prev, line]);
    };

    const formatDuration = (totalSeconds) => {
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    // Same pattern as MT form logs (CreationLogsModal): add timestamp if missing
    const getLogTimestamp = (log) => {
        if (log.match(/^\[\d{1,2}:\d{2}:\d{2}\s?[AP]?[M]?\]/)) {
            return log;
        }
        const timestamp = new Date().toLocaleTimeString();
        return `[${timestamp}] ${log}`;
    };

    const getLogStyle = (log) => {
        if (log.includes('❌') || (log.includes('Error') && log.includes('failed'))) {
            return { color: '#dc3545', fontWeight: 'bold' };
        }
        if (log.includes('✅')) {
            return { color: '#28a745', fontWeight: 'bold' };
        }
        if (log.includes('⚠️')) {
            return { color: '#ffc107', fontWeight: 'bold' };
        }
        return { color: '#212529' };
    };

    // Process log lines with timestamps (same pattern as MT form / CreationLogsModal)
    useEffect(() => {
        setProcessedLogLines(logLines.map((line) => getLogTimestamp(line)));
    }, [logLines]);

    useEffect(() => {
        if (!logModalOpen) return;
        if (!logContainerRef.current) return;
        logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }, [processedLogLines, logModalOpen]);

    // Elapsed timer for log modal
    useEffect(() => {
        if (!logModalOpen) return;
        if (logStatus === 'running') {
            logStartTimeRef.current = Date.now();
            logTimerRef.current = setInterval(() => {
                if (logStartTimeRef.current) {
                    setElapsedSeconds(Math.floor((Date.now() - logStartTimeRef.current) / 1000));
                }
            }, 1000);
        }
        return () => {
            if (logTimerRef.current) {
                clearInterval(logTimerRef.current);
                logTimerRef.current = null;
            }
        };
    }, [logModalOpen, logStatus]);

    /**
     * Build the payload expected by the external license API.
     * This does NOT change the existing payload used for backend logging/history.
     */
    const buildExternalLicensePayload = () => {
        // Map opportunity type to expected lowercase form (new | renewal | cancel)
        const normalizedOpportunity =
            typeof opportunityType === 'string'
                ? opportunityType.toLowerCase()
                : opportunityType;

        const externalProducts = {};

        Object.entries(productCatalog).forEach(([productName, productConfig]) => {
            const productState = productSelection[productName];
            if (!productState || !productConfig?.apiCategory) return;

            const apiCategory = productConfig.apiCategory;
            if (!externalProducts[apiCategory]) {
                externalProducts[apiCategory] = [];
            }

            if (productConfig.selectionMode === 'productQuantity') {
                if (!(productState.selected || Number(productState.quantity) > 0)) return;

                const featureAddons = [];
                const quantityFieldExtras = {};

                Object.entries(productState.addons || {}).forEach(([addonName, addonState]) => {
                    const addonDef = getAddonDefinition(productConfig, addonName);
                    const isActive =
                        !!addonState && (addonState.selected || Number(addonState.userCount) > 0);
                    if (!isActive || !addonDef?.apiKey) return;

                    featureAddons.push(addonDef.apiKey);
                    if (addonDef.hasQuantity && addonDef.quantityField) {
                        quantityFieldExtras[addonDef.quantityField] =
                            Number(addonState.userCount) || 0;
                    }
                });

                const addons =
                    featureAddons.length > 0
                        ? featureAddons
                        : productConfig.defaultAddonApiKeys || [];

                externalProducts[apiCategory].push({
                    productName: productConfig.apiProductName,
                    quantity: Number(productState.quantity) || 0,
                    ...(addons.length ? { addons } : {}),
                    ...quantityFieldExtras
                });
                return;
            }

            // perAddon: quantity lives on each addon (M365 / Google Workspace style)
            Object.entries(productState.addons || {}).forEach(([addonName, addonState]) => {
                if (!addonState) return;

                const addonDef = getAddonDefinition(productConfig, addonName);
                const hasUsers = Number(addonState.userCount) > 0;
                const selectedSubaddons = Object.keys(addonState.subaddons || {}).filter(
                    (name) => !!addonState.subaddons[name]
                );
                const hasAnySelection =
                    addonState.selected || hasUsers || selectedSubaddons.length > 0;
                if (!hasAnySelection) return;

                const subaddonApiByName = {};
                (addonDef?.subaddons || []).forEach((sub) => {
                    if (sub?.name && sub?.apiKey) {
                        subaddonApiByName[sub.name] = sub.apiKey;
                    }
                });

                const apiAddons = selectedSubaddons
                    .map((name) => subaddonApiByName[name])
                    .filter(Boolean);

                const productEntry = {
                    productName: addonDef?.apiProductName,
                    quantity: Number(addonState.userCount) || 0,
                    ...(apiAddons.length ? { addons: apiAddons } : {})
                };

                if (addonHasStorageGb(addonDef, addonName)) {
                    const storageField = getAddonStorageField(addonDef);
                    productEntry[storageField] = Number(addonState.storageGb) || 0;
                }

                externalProducts[apiCategory].push(productEntry);
            });
        });

        // Storage mapping for external API
        let externalStorage = null;
        if (storageType === 'Storage') {
            const sizeGb = Number(storageGb) || 0;
            const dataCenter = storageAddons.microsoft ? 'microsoft' : 'crashPlan';
            externalStorage = {
                type: 'storageGb',
                sizeGb,
                dataCenter
            };
        } else if (storageType === 'BYOS') {
            externalStorage = {
                type: 'byos',
                sizeGb: 0,
                dataCenter: null
            };
        }

        // Remove empty product categories so we don't send keys like "virtualMachine": []
        const cleanedProducts = Object.fromEntries(
            Object.entries(externalProducts).filter(
                ([, items]) => Array.isArray(items) && items.length > 0
            )
        );

        const basePayload = {
            opportunityType: normalizedOpportunity,
            validityPeriodDate: validityDate,
            products: cleanedProducts,
            ...(externalStorage ? { storage: externalStorage } : {})
        };

        const effectiveCloudName = (customerName || '').trim();

        const externalPayload = selectedCloudMeta
            ? {
                // Order is important for readability in logs:
                cloudName: (selectedCloudMeta.cloudName || customerName || '').trim(),
                customerName: selectedCloudMeta.customerName || null,
                countryCode: selectedCloudMeta.countryCode || null,
                // profile intentionally omitted for now
                ...basePayload
            }
            : {
                cloudName: effectiveCloudName,
                ...basePayload
            };

        // For now, just log this so it is easy to copy when wiring the real API.
        // eslint-disable-next-line no-console
        console.log('External license API payload:', externalPayload);

        return externalPayload;
    };

    const buildSelectedProductsPayload = () => {
        const products = [];

        Object.keys(productSelection).forEach((productName) => {
            const productState = productSelection[productName];

            const selectedAddons = [];

            Object.keys(productState.addons).forEach((addonName) => {
                const addonState = productState.addons[addonName];
                const subaddonsState = addonState.subaddons || {};

                const selectedSubaddons = Object.keys(subaddonsState).filter(
                    (subaddonName) => !!subaddonsState[subaddonName]
                );

                const isAddonSelected =
                    addonState.selected || selectedSubaddons.length > 0;

                if (isAddonSelected) {
                    const addonEntry = {
                        name: addonName,
                        users: Number(addonState.userCount) || 0,
                        subaddons: selectedSubaddons
                    };
                    const addonDef = getAddonDefinition(
                        productCatalog[productName],
                        addonName
                    );
                    const storageValue = Number(addonState.storageGb) || 0;
                    if (storageValue > 0 && addonHasStorageGb(addonDef, addonName)) {
                        addonEntry.storageGb = storageValue;
                    }
                    selectedAddons.push(addonEntry);
                }
            });

            const isProductSelected =
                productState.selected || selectedAddons.length > 0;

            if (isProductSelected) {
                products.push({
                    name: productName,
                    quantity: Number(productState.quantity) || 0,
                    addons: selectedAddons
                });
            }
        });

        return products;
    };

    const toggleProduct = (productName) => {
        setProductSelection((prev) => {
            const next = JSON.parse(JSON.stringify(prev));
            const current = !!next[productName].selected;
            next[productName].selected = !current;

            // If turning off a product, clear all nested selections
            if (!next[productName].selected) {
                next[productName].quantity = '';
                Object.keys(next[productName].addons).forEach((addonName) => {
                    next[productName].addons[addonName].selected = false;
                    next[productName].addons[addonName].userCount = '';
                    next[productName].addons[addonName].storageGb = '';
                    Object.keys(next[productName].addons[addonName].subaddons).forEach(
                        (subaddonName) => {
                            next[productName].addons[addonName].subaddons[subaddonName] = false;
                        }
                    );
                });
            }

            return next;
        });
    };

    const toggleAddon = (productName, addonName) => {
        setProductSelection((prev) => {
            const next = JSON.parse(JSON.stringify(prev));

            // Ensure product is selected if any addon is toggled
            next[productName].selected = true;

            const current = !!next[productName].addons[addonName].selected;
            next[productName].addons[addonName].selected = !current;

            const isM365Exchange =
                productName === 'M365 Enterprise' && addonName === 'Exchange';
            const isGmail =
                productName === 'Google Workspace' && addonName === 'Gmail';

            // For M365 Enterprise -> Exchange and Google Workspace -> Gmail,
            // enforce mandatory sub-addons when addon is enabled
            if (next[productName].addons[addonName].selected) {
                if (isM365Exchange) {
                    if (next[productName].addons[addonName].subaddons['eDiscovery / Search'] !== undefined) {
                        next[productName].addons[addonName].subaddons['eDiscovery / Search'] = true;
                    }
                    if (next[productName].addons[addonName].subaddons['In-Place Archive'] !== undefined) {
                        next[productName].addons[addonName].subaddons['In-Place Archive'] = true;
                    }
                }
                if (isGmail) {
                    if (next[productName].addons[addonName].subaddons['eDiscovery / Search'] !== undefined) {
                        next[productName].addons[addonName].subaddons['eDiscovery / Search'] = true;
                    }
                }
            }

            // If turning off an addon, clear its sub-addons, users, and storage
            if (!next[productName].addons[addonName].selected) {
                next[productName].addons[addonName].userCount = '';
                next[productName].addons[addonName].storageGb = '';
                Object.keys(next[productName].addons[addonName].subaddons).forEach(
                    (subaddonName) => {
                        next[productName].addons[addonName].subaddons[subaddonName] = false;
                    }
                );
            }

            return next;
        });
    };

    const toggleSubaddon = (productName, addonName, subaddonName) => {
        setProductSelection((prev) => {
            // Prevent unselecting mandatory sub-addons for:
            // - M365 Enterprise -> Exchange: eDiscovery / Search, In-Place Archive
            // - Google Workspace -> Gmail: eDiscovery / Search
            const isM365Exchange =
                productName === 'M365 Enterprise' && addonName === 'Exchange';
            const isGmail =
                productName === 'Google Workspace' && addonName === 'Gmail';
            if (
                (isM365Exchange &&
                    (subaddonName === 'eDiscovery / Search' || subaddonName === 'In-Place Archive')) ||
                (isGmail && subaddonName === 'eDiscovery / Search')
            ) {
                return prev;
            }

            const next = JSON.parse(JSON.stringify(prev));

            // Ensure parent product and addon are selected
            next[productName].selected = true;
            next[productName].addons[addonName].selected = true;

            const current =
                !!next[productName].addons[addonName].subaddons[subaddonName];
            next[productName].addons[addonName].subaddons[subaddonName] = !current;

            return next;
        });
    };

    const handleAddonUsersChange = (productName, addonName, value) => {
        setProductSelection((prev) => {
            const next = JSON.parse(JSON.stringify(prev));

            const numeric = String(value || '').replace(/[^\d]/g, '');
            next[productName].addons[addonName].userCount = numeric;

            // If user enters a count, ensure product and addon are marked selected
            if (numeric) {
                next[productName].selected = true;
                next[productName].addons[addonName].selected = true;

                const isM365Exchange =
                    productName === 'M365 Enterprise' && addonName === 'Exchange';
                const isGmail =
                    productName === 'Google Workspace' && addonName === 'Gmail';

                // For M365 Enterprise -> Exchange and Google Workspace -> Gmail,
                // also enforce mandatory sub-addons when users count is set
                if (isM365Exchange) {
                    if (next[productName].addons[addonName].subaddons['eDiscovery / Search'] !== undefined) {
                        next[productName].addons[addonName].subaddons['eDiscovery / Search'] = true;
                    }
                    if (next[productName].addons[addonName].subaddons['In-Place Archive'] !== undefined) {
                        next[productName].addons[addonName].subaddons['In-Place Archive'] = true;
                    }
                }
                if (isGmail) {
                    if (next[productName].addons[addonName].subaddons['eDiscovery / Search'] !== undefined) {
                        next[productName].addons[addonName].subaddons['eDiscovery / Search'] = true;
                    }
                }
            }

            return next;
        });
    };

    const handleAddonStorageChange = (productName, addonName, value) => {
        setProductSelection((prev) => {
            const next = JSON.parse(JSON.stringify(prev));
            const numeric = String(value || '').replace(/[^\d]/g, '');
            next[productName].addons[addonName].storageGb = numeric;
            if (numeric) {
                next[productName].selected = true;
                next[productName].addons[addonName].selected = true;
            }
            return next;
        });
    };

    const handleStorageTypeChange = (e, { value }) => {
        setStorageType(value);
    };

    const handleStorageGbChange = (e, { value }) => {
        const numeric = String(value || '').replace(/[^\d]/g, '');
        setStorageGb(numeric);
    };

    const toggleStorageAddon = (key) => {
        setStorageAddons((prev) => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handleProductQuantityChange = (productName, value) => {
        setProductSelection((prev) => {
            const next = JSON.parse(JSON.stringify(prev));
            const numeric = String(value || '').replace(/[^\d]/g, '');
            next[productName].quantity = numeric;
            if (numeric) {
                next[productName].selected = true;
            }
            return next;
        });
    };

    const validateBeforeGenerate = () => {
        const errors = [];
        if (!opportunityType) {
            errors.push('Opportunity Type is required.');
        }
        if (!validityDate) {
            errors.push('License Expiration Date is required.');
        }

        // Storage *
        if (!storageType) {
            errors.push('Storage is required. Select Storage (GB) or No Storage (BYOS).');
        } else if (storageType === 'Storage') {
            const gb = Number(storageGb) || 0;
            if (gb <= 0) {
                errors.push('Storage (GB) must be greater than 0.');
            }
        }

        // Add Products *
        const anyProductSelected = Object.keys(productCatalog).some(
            (name) => !!productSelection?.[name]?.selected
        );
        if (!anyProductSelected) {
            errors.push('Add Products is required. Select at least one product.');
        }

        Object.keys(productCatalog).forEach((productName) => {
            const productState = productSelection?.[productName];
            if (!productState?.selected) return;

            const productConfig = productCatalog[productName] || {};
            const hasMainQty = productConfig.selectionMode === 'productQuantity';
            const addonsConfig = productConfig.addons || {};

            if (hasMainQty) {
                const qty = Number(productState.quantity) || 0;
                if (qty <= 0) {
                    errors.push(`${productName}: Quantity is required and must be greater than 0.`);
                }

                Object.keys(addonsConfig).forEach((addonName) => {
                    const addonDef = getAddonDefinition(productConfig, addonName);
                    if (!addonDef?.hasQuantity) return;
                    const addonState = productState.addons?.[addonName];
                    if (!addonState?.selected) return;
                    const addonQty = Number(addonState.userCount) || 0;
                    if (addonQty <= 0) {
                        errors.push(
                            `${productName} -> ${addonName}: Quantity is required and must be greater than 0.`
                        );
                    }
                });

                return;
            }

            // Non-main-quantity products: any selected addon requires Quantity *
            const addonNames = Object.keys(addonsConfig);
            let hasAnyAddonSelected = false;

            addonNames.forEach((addonName) => {
                const addonState = productState.addons?.[addonName];
                if (!addonState) return;

                const subaddonsState = addonState.subaddons || {};
                const selectedSubaddons = Object.keys(subaddonsState).filter(
                    (subaddonName) => !!subaddonsState[subaddonName]
                );
                const isAddonSelected = !!addonState.selected || selectedSubaddons.length > 0;
                if (!isAddonSelected) return;

                hasAnyAddonSelected = true;
                const qty = Number(addonState.userCount) || 0;
                if (qty <= 0) {
                    errors.push(`${productName} -> ${addonName}: Quantity is required and must be greater than 0.`);
                }

                const addonDef = getAddonDefinition(productConfig, addonName);
                if (addonHasStorageGb(addonDef, addonName)) {
                    const storageValue = Number(addonState.storageGb) || 0;
                    if (storageValue <= 0) {
                        errors.push(
                            `${productName} -> ${addonName}: Storage (GB) is required and must be greater than 0.`
                        );
                    }
                }
            });

            if (!hasAnyAddonSelected) {
                errors.push(`${productName}: select at least one add-on and provide its quantity.`);
            }
        });

        return errors.length ? errors.join(' ') : null;
    };

    const validateCloudFields = (cloudName) => {
        const hasCloud = !!(cloudName || '').trim();
        if (!hasCloud) {
            return 'Cloud name is required.';
        }
        return '';
    };

    const handleSubmit = async () => {
        const cloudError = validateCloudFields(customerName);
        if (cloudError) {
            setCloudFieldError(cloudError);
            return;
        }

        setCloudFieldError('');

        const validationError = validateBeforeGenerate();
        if (validationError) {
            setError(validationError);
            return;
        }

        setError(null);
        setSubmitted(false);
        setLogLines([]);
        setElapsedSeconds(0);
        setLogStatus('running');
        setLogModalOpen(true);

        appendLogLine('Starting license generation process...');

        const productsPayload = buildSelectedProductsPayload();
        appendLogLine('Built internal products payload for history.');

        const storagePayload = {
            type: storageType,
            gb: storageType === 'Storage' ? Number(storageGb) || 0 : 0,
            addons: {
                crashPlanDataCenter: storageType === 'Storage' ? !!storageAddons.crashPlan : false,
                microsoftDataCenter: storageType === 'Storage' ? !!storageAddons.microsoft : false
            }
        };
        appendLogLine('Processed storage configuration.');

        // Build payload for the external license API and invoke it
        appendLogLine('Preparing external license API payload...');
        const externalPayload = buildExternalLicensePayload();
        appendLogLine('External license API payload ready.');

        try {
            appendLogLine('Calling license API via backend (using provisioning credentials)...');
            const externalResponse = await api.licenseGenerator.generateExternalLicense(
                externalPayload
            );
            const resData = externalResponse?.data;
            if (Array.isArray(resData?.logs)) {
                resData.logs.forEach((line) => appendLogLine(line));
            }
            if (resData?.success !== false && resData?.data != null) {
                try {
                    appendLogLine(`License API response: ${JSON.stringify(resData.data)}`);
                } catch {
                    appendLogLine('License API response received (unable to stringify).');
                }
            }
            appendLogLine('License API call completed successfully.');
        } catch (externalErr) {
            const resData = externalErr?.response?.data;
            if (Array.isArray(resData?.logs)) {
                resData.logs.forEach((line) => appendLogLine(line));
            }
            const msg =
                resData?.message ||
                externalErr?.message ||
                'License API call failed.';
            setError(msg);
            setSubmitted(false);
            setLogStatus('error');
            appendLogLine(`License API call failed: ${msg}`);
            return;
        }

        const payload = {
            customerName: (customerName || '').trim(),
            opportunityType,
            validityPeriodDate: validityDate,
            products: productsPayload,
            storage: storagePayload,
            generatedBy: userEmail || userName || '',
            // Persist the exact request body that was sent to the external license API
            licenseApiPayload: externalPayload
        };

        try {
            appendLogLine('Logging license generation event to backend...');
            await api.licenseGenerator.logGeneration(payload);
            appendLogLine('License generation event logged successfully.');

            // Capture cloud name used for license so the completion modal can offer a download button
            const finalCloudName =
                (externalPayload &&
                    typeof externalPayload.cloudName === 'string' &&
                    externalPayload.cloudName.trim()) ||
                (customerName || '').trim();
            if (finalCloudName) {
                setLicenseDetailsCloudName(finalCloudName);
            }

            setSubmitted(true);
            setLogStatus('success');
            appendLogLine('License generation completed successfully.');

            // After success, auto-clear and go back to Step 1
            scheduleReset();
        } catch (err) {
            // Surface error to user but keep their input so they can retry
            // eslint-disable-next-line no-console
            console.error('Failed to log license generation event:', err);
            setError(
                err?.response?.data?.message ||
                'Failed to log license generation. Please try again.'
            );
            setSubmitted(false);
            setLogStatus('error');
            appendLogLine(
                `License generation failed: ${
                    err?.response?.data?.message || err?.message || 'Unknown error'
                }`
            );
        }
    };

    const renderContent = () => {
        const hasAnyProductSelected = Object.keys(productCatalog).some(
            (productName) => !!productSelection?.[productName]?.selected
        );
        const hasAnyCloudName =
            customerName.trim().length > 0;
        const canGenerate =
            hasAnyCloudName &&
            !!opportunityType &&
            !!validityDate &&
            !!storageType &&
            hasAnyProductSelected &&
            !isLoadingClouds &&
            !cloudFieldError;

        const todayStr = new Date().toISOString().split('T')[0];

        return (
        <div>
            <Form>
                        <Form.Field required error={!!cloudFieldError}>
                            <label>Cloud Name</label>
                            <Form.Dropdown
                                placeholder="Search cloud or type a new cloud name"
                                fluid
                                search
                                selection
                                clearable
                                options={cloudOptionsWithAddAction}
                                value={customerName || null}
                                searchQuery={cloudQuery}
                                loading={isLoadingClouds}
                                noResultsMessage={
                                    cloudQuery?.trim()
                                        ? 'No matching cloud found. Press Enter to use this value.'
                                        : 'No clouds found'
                                }
                                onOpen={() => {
                                    // Load full list on first open
                                    if (cloudOptions.length === 0) {
                                        loadCloudOptions(cloudQuery);
                                    }
                                }}
                                onSearchChange={(e, { searchQuery }) => {
                                    const q = (searchQuery || '').toString();
                                    setCloudQuery(q);
                                    // Accept direct user input even when not present in search results.
                                    setCustomerName(q);
                                    setSelectedCloudMeta(null);
                                    setCloudFieldError('');

                                    if (cloudSearchDebounceRef.current) {
                                        clearTimeout(cloudSearchDebounceRef.current);
                                    }
                                    cloudSearchDebounceRef.current = setTimeout(() => {
                                        loadCloudOptions(q.trim());
                                    }, 300);
                                }}
                                onChange={(e, { value }) => {
                                    const rawValue = (value || '').toString();
                                    const selectedName = rawValue.startsWith(ADD_NEW_CLOUD_PREFIX)
                                        ? rawValue.slice(ADD_NEW_CLOUD_PREFIX.length).trim()
                                        : rawValue.trim();
                                    if (rawValue.startsWith(ADD_NEW_CLOUD_PREFIX) && selectedName) {
                                        setCloudOptions((prev) => {
                                            if (prev.some((opt) => (opt?.value || '').toString() === selectedName)) return prev;
                                            return [...prev, { key: selectedName, value: selectedName, text: selectedName }];
                                        });
                                    }
                                    setCustomerName(selectedName);
                                    setCloudQuery(selectedName);
                                    setSelectedCloudMeta(selectedName ? cloudMetaByName[selectedName] || null : null);
                                    const err = validateCloudFields(selectedName);
                                    setCloudFieldError(err);
                                }}
                            />
                            {cloudFieldError && (
                                <div className="ui pointing red basic label" style={{ marginTop: '0.4rem' }}>
                                    {cloudFieldError}
                                </div>
                            )}
                        </Form.Field>

                {/*
                <Form.Select
                    required
                    label="Opportunity Type"
                    placeholder="Select opportunity type"
                    options={opportunityTypes}
                    value={opportunityType}
                    onChange={(e, { value }) => setOpportunityType(value)}
                />
                */}

                <Form.Input
                    required
                    type="date"
                    label="License Expiration Date"
                    value={validityDate}
                    onChange={(e, { value }) => setValidityDate(value)}
                    min={todayStr}
                    style={{ width: '220px' }}
                />

                <Segment
                    raised
                    style={{
                        marginTop: '1.5rem',
                        borderRadius: '10px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.04)',
                        padding: '1rem 1.2rem',
                        background:
                            'linear-gradient(135deg, rgba(6,45,77,0.02), rgba(53,183,244,0.03))'
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            marginBottom: '0.5rem',
                            gap: '0.3rem'
                        }}
                    >
                        <Header
                            as="h4"
                            style={{
                                margin: 0,
                                color: '#062D4D'
                            }}
                        >
                            Add Products
                        </Header>
                        <span
                            style={{
                                color: '#dc2626',
                                fontSize: '1rem',
                                lineHeight: 1
                            }}
                        >
                            *
                        </span>
                    </div>
                    <div
                        style={{
                            marginBottom: '0.75rem',
                            color: '#4b5563',
                            fontSize: '0.9rem'
                        }}
                    >
                        Choose main products first, then expand into addons and sub-addons as needed.
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.6rem'
                        }}
                    >
                    {Object.keys(productCatalog).map((productName) => {
                        const productState = productSelection[productName] || {
                            selected: false,
                            quantity: '',
                            addons: {}
                        };
                        const productConfig = productCatalog[productName] || {};
                        const addons = productConfig.addons || {};

                        const hasMainQuantity = productConfig.selectionMode === 'productQuantity';

                        return (
                            <div
                                key={productName}
                                style={{
                                    borderRadius: '8px',
                                    border: productState.selected
                                        ? '1px solid rgba(53,183,244,0.9)'
                                        : '1px solid #e5e7eb',
                                    backgroundColor: productState.selected
                                        ? 'rgba(53,183,244,0.04)'
                                        : '#ffffff',
                                    padding: '0.7rem 0.85rem',
                                    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                                    boxShadow: productState.selected
                                        ? '0 1px 4px rgba(15,23,42,0.08)'
                                        : 'none'
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        flexWrap: 'wrap',
                                        rowGap: '0.35rem'
                                    }}
                                >
                                    <Checkbox
                                        label={productName}
                                        checked={productState.selected}
                                        onChange={() => toggleProduct(productName)}
                                        style={{
                                            margin: 0,
                                            fontWeight: 600,
                                            width: 'fit-content',
                                            flex: '0 0 auto'
                                        }}
                                    />
                                    {hasMainQuantity && productState.selected && (
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.35rem',
                                                marginLeft: '0.1rem'
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontSize: '0.8rem',
                                                    color: '#4b5563'
                                                }}
                                            >
                                                Quantity
                                                <span
                                                    style={{
                                                        color: '#dc2626',
                                                        marginLeft: '2px'
                                                    }}
                                                >
                                                    *
                                                </span>
                                                :
                                            </span>
                                            <Form.Input
                                                type="number"
                                                min={0}
                                                step={1}
                                                input={{ inputMode: 'numeric' }}
                                                value={productState.quantity || ''}
                                                onChange={(e, { value }) =>
                                                    handleProductQuantityChange(productName, value)
                                                }
                                                placeholder="0"
                                                style={{
                                                    width: '70px',
                                                    margin: 0
                                                }}
                                                size="small"
                                            />
                                        </div>
                                    )}
                                </div>
                                {productState.selected && (
                                    <div
                                        style={{
                                            marginTop: '0.4rem',
                                            paddingTop: '0.35rem',
                                            borderTop: '1px dashed #e5e7eb'
                                        }}
                                    >
                                        {Object.keys(addons).map((addonName) => {
                                            const addonState =
                                                productState.addons?.[addonName] || {
                                                    selected: false,
                                                    subaddons: {}
                                                };
                                            const subaddons = addons[addonName] || [];
                                            const addonDef = getAddonDefinition(
                                                productConfig,
                                                addonName
                                            );
                                            const isQuantityAddon = !!addonDef?.hasQuantity;
                                            const showAddonQuantity =
                                                (!hasMainQuantity && addonState.selected) ||
                                                (isQuantityAddon && addonState.selected);
                                            const showAddonStorage =
                                                addonHasStorageGb(addonDef, addonName) &&
                                                addonState.selected;

                                            return (
                                                <div
                                                    key={addonName}
                                                    style={{
                                                        marginTop: '0.3rem',
                                                        padding: '0.2rem 0.25rem',
                                                        borderRadius: '6px'
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'flex-start',
                                                            gap: '0.5rem',
                                                            flexWrap: 'wrap',
                                                            rowGap: '0.35rem'
                                                        }}
                                                    >
                                                        <Checkbox
                                                            label={addonName}
                                                            checked={addonState.selected}
                                                            onChange={() =>
                                                                toggleAddon(productName, addonName)
                                                            }
                                                            style={{
                                                                margin: 0,
                                                                width: 'fit-content',
                                                                flex: '0 0 auto'
                                                            }}
                                                        />
                                                        {showAddonQuantity && (
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '0.35rem',
                                                                    marginLeft: '0.1rem'
                                                                }}
                                                            >
                                                                <span
                                                                    style={{
                                                                        fontSize: '0.8rem',
                                                                        color: '#4b5563'
                                                                    }}
                                                                >
                                                                    Quantity
                                                                    <span
                                                                        style={{
                                                                            color: '#dc2626',
                                                                            marginLeft: '2px'
                                                                        }}
                                                                    >
                                                                        *
                                                                    </span>
                                                                    :
                                                                </span>
                                                                <Form.Input
                                                                    type="number"
                                                                    min={0}
                                                                    step={1}
                                                                    input={{ inputMode: 'numeric' }}
                                                                    value={addonState.userCount || ''}
                                                                    onChange={(e, { value }) =>
                                                                        handleAddonUsersChange(
                                                                            productName,
                                                                            addonName,
                                                                            value
                                                                        )
                                                                    }
                                                                    placeholder="0"
                                                                    style={{
                                                                        width: '70px',
                                                                        margin: 0
                                                                    }}
                                                                    size="small"
                                                                />
                                                            </div>
                                                        )}
                                                        {showAddonStorage && (
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '0.35rem',
                                                                    marginLeft: '0.1rem'
                                                                }}
                                                            >
                                                                <span
                                                                    style={{
                                                                        fontSize: '0.8rem',
                                                                        color: '#4b5563'
                                                                    }}
                                                                >
                                                                    Storage (GB)
                                                                    <span
                                                                        style={{
                                                                            color: '#dc2626',
                                                                            marginLeft: '2px'
                                                                        }}
                                                                    >
                                                                        *
                                                                    </span>
                                                                    :
                                                                </span>
                                                                <Form.Input
                                                                    type="number"
                                                                    min={0}
                                                                    step={1}
                                                                    input={{ inputMode: 'numeric' }}
                                                                    value={addonState.storageGb || ''}
                                                                    onChange={(e, { value }) =>
                                                                        handleAddonStorageChange(
                                                                            productName,
                                                                            addonName,
                                                                            value
                                                                        )
                                                                    }
                                                                    placeholder="0"
                                                                    style={{
                                                                        width: '70px',
                                                                        margin: 0
                                                                    }}
                                                                    size="small"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                    {addonState.selected && subaddons.length > 0 && (
                                                        <div
                                                            style={{
                                                                marginLeft: '1.25rem',
                                                                marginTop: '0.2rem'
                                                            }}
                                                        >
                                                            {subaddons.map((subaddonName) => {
                                                                const isMandatorySubaddon =
                                                                    (productName === 'M365 Enterprise' &&
                                                                        addonName === 'Exchange' &&
                                                                        (subaddonName === 'eDiscovery / Search' ||
                                                                            subaddonName === 'In-Place Archive')) ||
                                                                    (productName === 'Google Workspace' &&
                                                                        addonName === 'Gmail' &&
                                                                        subaddonName === 'eDiscovery / Search');

                                                                const commonProps = {
                                                                    key: subaddonName,
                                                                    label: subaddonName,
                                                                    checked:
                                                                        addonState.subaddons?.[
                                                                            subaddonName
                                                                        ] || false,
                                                                    style: {
                                                                        marginTop: '0.15rem',
                                                                        ...(isMandatorySubaddon
                                                                            ? {
                                                                                  opacity: 1,
                                                                                  cursor: 'default',
                                                                                  color: '#374151',
                                                                                  fontWeight: 600
                                                                              }
                                                                            : {})
                                                                    }
                                                                };

                                                                if (isMandatorySubaddon) {
                                                                    // Greyed out & non-interactive to indicate default enabled
                                                                    return (
                                                                        <Form.Checkbox
                                                                            {...commonProps}
                                                                            disabled
                                                                        />
                                                                    );
                                                                }

                                                                return (
                                                                    <Form.Checkbox
                                                                        {...commonProps}
                                                                        onChange={() =>
                                                                            toggleSubaddon(
                                                                                productName,
                                                                                addonName,
                                                                                subaddonName
                                                                            )
                                                                        }
                                                                    />
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    </div>
                </Segment>

                <Segment
                    raised
                    style={{
                        marginTop: '1rem',
                        borderRadius: '10px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 3px 10px rgba(15, 23, 42, 0.04)',
                        padding: '1rem 1.2rem'
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.3rem',
                            marginBottom: '0.5rem'
                        }}
                    >
                        <Header
                            as="h4"
                            style={{
                                margin: 0,
                                color: '#062D4D'
                            }}
                        >
                            Storage
                        </Header>
                        <span
                            style={{
                                color: '#dc2626',
                                fontSize: '1rem',
                                lineHeight: 1
                            }}
                        >
                            *
                        </span>
                    </div>
                    <div
                        style={{
                            marginBottom: '0.75rem',
                            color: '#4b5563',
                            fontSize: '0.9rem'
                        }}
                    >
                        Choose how storage is handled for this license.
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <Form.Radio
                            label="No Storage (BYOS)"
                            value="BYOS"
                            checked={storageType === 'BYOS'}
                            onChange={handleStorageTypeChange}
                        />
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                gap: '0.75rem',
                                flexWrap: 'nowrap'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', flex: '0 0 auto' }}>
                                <Form.Radio
                                    label="Storage (GB)"
                                    value="Storage"
                                    checked={storageType === 'Storage'}
                                    onChange={handleStorageTypeChange}
                                    style={{ margin: 0 }}
                                />
                            </div>
                            {storageType === 'Storage' && (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.35rem',
                                        marginLeft: '0.1rem',
                                        flex: '0 0 auto'
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: '0.8rem',
                                            color: '#4b5563',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        Quantity
                                        <span
                                            style={{
                                                color: '#dc2626',
                                                marginLeft: '2px'
                                            }}
                                        >
                                            *
                                        </span>
                                        :
                                    </span>
                                    <Form.Input
                                        type="number"
                                        min={0}
                                        step={1}
                                        input={{ inputMode: 'numeric' }}
                                        value={storageGb}
                                        onChange={handleStorageGbChange}
                                        placeholder="0"
                                        style={{ width: '100px', margin: 0 }}
                                        size="small"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </Segment>

            {error && (
                <Message negative>
                    <Message.Header>Missing details</Message.Header>
                    <p>{error}</p>
                </Message>
            )}

            {submitted && !error && (
                <Message positive>
                    <Message.Header>License generated</Message.Header>
                    <p>
                        License details have been generated. You will receive the license
                        details at your registered email address.
                    </p>
                </Message>
            )}

            <div
                style={{
                    marginTop: '1.5rem',
                    display: 'flex',
                    justifyContent: 'space-between'
                }}
            >
                <div>
                    <Button basic type="button" onClick={resetForm} style={{ marginLeft: '0.75rem' }}>
                        <Icon name="erase" style={{ marginRight: '0.5rem' }} />
                        Clear
                    </Button>
                </div>
                <div>
                    <Button
                        color="green"
                        onClick={handleSubmit}
                        disabled={!canGenerate}
                        loading={isLoadingClouds}
                    >
                        Generate License
                        <Icon name="check" style={{ marginLeft: '0.5rem' }} />
                    </Button>
                </div>
            </div>
            </Form>
        </div>
    );
    };

    return (
        <div
            style={{
                padding: '1.5rem',
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)'
            }}
        >
            {/* Summary header */}
            <div style={{ marginBottom: '1.25rem' }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '1.35rem',
                        fontWeight: 700,
                        color: '#062D4D',
                        marginBottom: '0.2rem'
                    }}
                >
                    <Icon name="key" color="blue" />
                    <span>License Generator</span>
                </div>
                <div
                    style={{
                        marginTop: '0.35rem',
                        fontWeight: 500,
                        color: '#062D4D'
                    }}
                >
                    {activeTab === 'generator'
                        ? 'Enter details to generate a license'
                        : 'View license history for generated licenses'}
                </div>
            </div>

            <Menu pointing secondary style={{ marginBottom: '1rem' }}>
                <Menu.Item
                    name="Generate License"
                    active={activeTab === 'generator'}
                    onClick={() => setActiveTab('generator')}
                />
                <Menu.Item
                    name="License Details"
                    active={activeTab === 'history'}
                    onClick={() => setActiveTab('history')}
                />
            </Menu>

            {activeTab === 'generator' && renderContent()}
            {activeTab === 'history' && (
                <Segment>
                    <Form.Field style={{ marginBottom: '1rem' }}>
                        <label>Search by cloud name</label>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', maxWidth: '640px' }}>
                            <Form.Input
                                placeholder="Enter cloud name (e.g. cpgqa1)"
                                value={historySearchCloud}
                                onChange={(e, { value }) => setHistorySearchCloud((value || '').toString())}
                                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleHistorySearch())}
                                icon={historySearchLoading ? 'spinner' : 'search'}
                                iconPosition="left"
                                style={{ flex: 1, minWidth: 200 }}
                            />
                            <Button
                                content="Search"
                                primary
                                onClick={handleHistorySearch}
                                loading={historySearchLoading}
                                disabled={historySearchLoading}
                            />
                            {historySearchResults !== null && (
                                <Button
                                    content="Clear"
                                    onClick={() => {
                                        setHistorySearchCloud('');
                                        setHistorySearchResults(null);
                                        setHistorySearchError(null);
                                        setActivePage(1);
                                    }}
                                />
                            )}
                        </div>
                    </Form.Field>

                    {historySearchError && (
                        <Message negative style={{ marginBottom: '1rem' }}>
                            <Message.Header>Search error</Message.Header>
                            <p>{historySearchError}</p>
                        </Message>
                    )}

                    {historyLoading && historySearchResults === null && (
                        <Dimmer active inverted>
                            <Loader>Loading history...</Loader>
                        </Dimmer>
                    )}
                    {historyError && historySearchResults === null && (
                        <Message negative>
                            <Message.Header>Error</Message.Header>
                            <p>{historyError}</p>
                        </Message>
                    )}
                    {!historyLoading && !historyError && historySearchResults === null && historyList.length === 0 && (
                        <Message info>
                            <Message.Header>No licenses yet</Message.Header>
                            <p>License history will appear here after licenses are generated. Enter a cloud name above and click Search.</p>
                        </Message>
                    )}
                    {historySearchResults !== null && !historySearchLoading && historySearchResults.length === 0 && (
                        <Message info style={{ marginTop: '1rem' }}>
                            No licenses found for cloud name &quot;{(historySearchCloud || '').toString().trim()}&quot;.
                        </Message>
                    )}
                    {(() => {
                        const sourceList =
                            historySearchResults !== null
                                ? historySearchResults
                                : historyList;
                        const rows = (Array.isArray(sourceList) ? sourceList : []).filter(
                            (row) => typeof row === 'object' && row !== null && !Array.isArray(row)
                        );
                        if (rows.length === 0) return null;
                        const totalPages = Math.max(1, Math.ceil(rows.length / Math.max(1, rowsPerPage)));
                        const pageStart = (activePage - 1) * rowsPerPage;
                        const pageEnd = pageStart + rowsPerPage;
                        const visibleRows = rows.slice(pageStart, pageEnd);
                        return (
                            <>
                                <Table
                                    celled
                                    striped
                                    size="small"
                                    style={{ marginTop: 0, tableLayout: 'fixed', width: '100%' }}
                                >
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.HeaderCell style={{ width: '35%' }} textAlign="center">
                                                Cloud Name
                                            </Table.HeaderCell>
                                            <Table.HeaderCell style={{ width: '22%' }} textAlign="center">
                                                Date
                                            </Table.HeaderCell>
                                            {/*
                                            <Table.HeaderCell>Opportunity type</Table.HeaderCell>
                                            */}
                                            <Table.HeaderCell style={{ width: '43%' }} textAlign="center">
                                                Actions
                                            </Table.HeaderCell>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {visibleRows.map((row, idx) => (
                                            <Table.Row key={pageStart + idx}>
                                                <Table.Cell
                                                    textAlign="center"
                                                    style={{ wordBreak: 'break-word' }}
                                                >
                                                    {getHistoryCloudName(row) || '—'}
                                                </Table.Cell>
                                                <Table.Cell textAlign="center" style={{ whiteSpace: 'nowrap' }}>
                                                    {getHistoryDate(row) || '—'}
                                                </Table.Cell>
                                                {/*
                                                <Table.Cell>{getHistoryOpportunityType(row) || '—'}</Table.Cell>
                                                */}
                                                <Table.Cell textAlign="center">
                                                    {/*
                                                    <Button
                                                        size="small"
                                                        primary
                                                        onClick={() => openDetailsModal(row)}
                                                        disabled={!getHistoryRequestId(row)}
                                                        style={{ marginRight: '0.5rem' }}
                                                    >
                                                        <Icon name="eye" />
                                                        View Details
                                                    </Button>
                                                    */}
                                                    <Button
                                                        size="small"
                                                        onClick={() => openLicenseDetailsModal(row)}
                                                        disabled={!getHistoryCloudName(row)}
                                                    >
                                                        <Icon name="key" />
                                                        License History
                                                    </Button>
                                                </Table.Cell>
                                            </Table.Row>
                                        ))}
                                    </Table.Body>
                                </Table>
                                <PaginationFooter
                                    activePage={activePage}
                                    totalPages={totalPages}
                                    totalItems={rows.length}
                                    filteredItems={rows}
                                    hasActiveFilters={false}
                                    onPageChange={setActivePage}
                                    pageStart={pageStart}
                                    pageEnd={pageEnd}
                                />
                            </>
                        );
                    })()}
                </Segment>
            )}

            <Modal
                open={logModalOpen}
                size="large"
                className="logs-modal"
                closeOnDimmerClick={logStatus !== 'running'}
                onClose={() => {
                    if (logStatus === 'running') return;
                    setLogModalOpen(false);
                }}
            >
                <Modal.Header>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <div>
                                {logStatus === 'running'
                                    ? 'License generation is in progress...'
                                    : logStatus === 'success'
                                    ? 'License generation completed'
                                    : logStatus === 'error'
                                    ? 'License generation failed'
                                    : 'License Generation Log'}
                            </div>
                            {logModalOpen && logStartTimeRef.current != null && (
                                <div style={{ marginTop: '2px', fontSize: '0.6em', color: '#aaa', lineHeight: '1.1' }}>
                                    {logStatus === 'running'
                                        ? `Time elapsed: ${formatDuration(elapsedSeconds)}`
                                        : (logStatus === 'success' || logStatus === 'error') && elapsedSeconds > 0
                                        ? `Total time: ${formatDuration(elapsedSeconds)}`
                                        : null}
                                </div>
                            )}
                        </div>
                        {logStatus === 'running' && (
                            <div>
                                <Icon name="circle" color="green" size="small" className="blinking" /> Live
                            </div>
                        )}
                        {logStatus === 'success' && (
                            <div style={{ color: '#28a745', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                <Icon name="check circle" color="green" size="small" /> Completed
                            </div>
                        )}
                        {logStatus === 'error' && (
                            <div style={{ color: '#dc3545' }}>
                                <Icon name="times circle" color="red" size="small" /> Failed
                            </div>
                        )}
                    </div>
                </Modal.Header>
                <Modal.Content>
                    <div
                        ref={logContainerRef}
                        style={{
                            position: 'relative',
                            maxHeight: '400px',
                            overflowY: 'auto',
                            fontFamily: 'monospace',
                            whiteSpace: 'pre-wrap',
                            background: '#f5f5f5',
                            padding: '15px',
                            borderRadius: '4px'
                        }}
                    >
                        {processedLogLines.length === 0 && logStatus === 'running' && (
                            <Dimmer active>
                                <Loader>Loading</Loader>
                            </Dimmer>
                        )}
                        {processedLogLines.map((line, idx) => (
                            <div
                                key={`${idx}-${line.slice(0, 24)}`}
                                style={{
                                    ...getLogStyle(line),
                                    marginBottom: '8px',
                                    padding: '4px 0',
                                    borderBottom: '1px solid #eee'
                                }}
                            >
                                {line}
                            </div>
                        ))}
                    </div>
                </Modal.Content>
                <Modal.Actions>
                    {logStatus === 'success' && licenseDetailsCloudName && (
                        <Button
                            primary
                            loading={licenseDetailsDownloadingFileName === '__view__'}
                            disabled={licenseDetailsDownloadingFileName === '__latest__'}
                            onClick={() => openLicenseDetailsFromCloudName(licenseDetailsCloudName)}
                        >
                            <Icon name="eye" style={{ marginRight: '0.5em' }} />
                            View License
                        </Button>
                    )}
                    {logStatus === 'success' && licenseDetailsCloudName && (
                        <Button
                            primary
                            icon="download"
                            content="Download License"
                            loading={licenseDetailsDownloadingFileName === '__latest__'}
                            disabled={licenseDetailsDownloadingFileName === '__view__'}
                            onClick={() => downloadLatestLicenseForCloud(licenseDetailsCloudName)}
                        />
                    )}
                    <Button
                        onClick={() => setLogModalOpen(false)}
                        disabled={logStatus === 'running'}
                        color={logStatus === 'success' ? 'green' : 'grey'}
                    >
                        Close
                    </Button>
                </Modal.Actions>
            </Modal>

            {/* License request details modal (History tab) */}
            <Modal
                open={detailsModalOpen}
                onClose={closeDetailsModal}
                size="large"
            >
                <Modal.Header>License request details</Modal.Header>
                <Modal.Content>
                    {detailsLoading && (
                        <Dimmer active inverted>
                            <Loader>Loading details...</Loader>
                        </Dimmer>
                    )}
                    {detailsError && (
                        <Message negative>
                            <Message.Header>Error</Message.Header>
                            <p>{detailsError}</p>
                        </Message>
                    )}
                    {!detailsLoading && !detailsError && detailsData != null && (
                        <>
                            <div style={{ marginBottom: '0.75rem', display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    size="small"
                                    toggle
                                    active={detailsViewRaw}
                                    onClick={() => setDetailsViewRaw((v) => !v)}
                                >
                                    <Icon name="code" />
                                    {detailsViewRaw ? 'Show Formatted' : 'View raw data'}
                                </Button>
                            </div>
                            {detailsViewRaw ? (
                                <div
                                    style={{
                                        margin: 0,
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                        maxHeight: '70vh',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                                        border: '1px solid #333'
                                    }}
                                >
                                    <div
                                        style={{
                                            padding: '8px 12px',
                                            background: '#2d2d2d',
                                            borderBottom: '1px solid #444',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            fontFamily: 'monospace',
                                            fontSize: '12px',
                                            color: '#9ca3af'
                                        }}
                                    >
                                        <span>Raw data</span>
                                        <span style={{ color: '#6b7280', marginLeft: 4 }}>— response.json</span>
                                        <span
                                            style={{ marginLeft: 'auto', cursor: 'pointer', color: detailsRawCopied ? '#22c55e' : '#9ca3af' }}
                                            onClick={() => {
                                                const text = JSON.stringify(detailsData, null, 2);
                                                safeCopyToClipboard(text);
                                                setDetailsRawCopied(true);
                                                setTimeout(() => setDetailsRawCopied(false), 1000);
                                            }}
                                            role="button"
                                            aria-label="Copy raw data"
                                        >
                                            <Icon name={detailsRawCopied ? 'check' : 'copy'} />
                                        </span>
                                    </div>
                                    <pre
                                        style={{
                                            margin: 0,
                                            padding: '14px 16px',
                                            background: '#1a1a1a',
                                            color: '#d4d4d4',
                                            fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
                                            fontSize: '12px',
                                            lineHeight: 1.5,
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            overflow: 'auto',
                                            flex: 1,
                                            minHeight: 120
                                        }}
                                    >
                                        {JSON.stringify(detailsData, null, 2)}
                                    </pre>
                                </div>
                            ) : (
                                <LicenseRequestDetailsDesign data={detailsData} />
                            )}
                        </>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={closeDetailsModal}>Close</Button>
                </Modal.Actions>
            </Modal>

            {/* License history by cloud name modal (History tab) – redesigned */}
            <Modal
                open={licenseDetailsModalOpen}
                onClose={closeLicenseDetailsModal}
                size="large"
                closeOnDimmerClick
            >
                <Modal.Header style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '0.75rem' }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '0.75rem',
                            flexWrap: 'wrap'
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Icon name="key" />
                            <span style={{ fontWeight: 600 }}>License History</span>
                        </div>
                        {licenseDetailsCloudName && (
                            <Label
                                size="medium"
                                basic
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.35rem',
                                    marginLeft: 'auto',
                                    background: '#f9fafb',
                                    borderColor: '#e5e7eb',
                                    color: '#374151'
                                }}
                            >
                                <span style={{ fontWeight: 500 }}>Cloud:</span>
                                <span style={{ fontWeight: 600 }}>{licenseDetailsCloudName}</span>
                                <Icon
                                    name={licenseDetailsCloudCopied ? 'check' : 'copy'}
                                    style={{
                                        cursor: 'pointer',
                                        color: licenseDetailsCloudCopied ? '#22c55e' : '#9ca3af',
                                        transition: 'color 120ms ease-out'
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!licenseDetailsCloudName) return;
                                        safeCopyToClipboard(licenseDetailsCloudName);
                                        setLicenseDetailsCloudCopied(true);
                                        setTimeout(() => setLicenseDetailsCloudCopied(false), 1000);
                                    }}
                                    title="Copy cloud name"
                                    aria-label="Copy cloud name"
                                />
                            </Label>
                        )}
                    </div>
                </Modal.Header>
                <Modal.Content style={{ padding: '1.25rem 1.5rem' }}>
                    {licenseDetailsLoading && (
                        <Dimmer active inverted>
                            <Loader>Loading license history...</Loader>
                        </Dimmer>
                    )}
                    {licenseDetailsError && (
                        <Message negative>
                            <Message.Header>Error</Message.Header>
                            <p>{licenseDetailsError}</p>
                        </Message>
                    )}
                    {!licenseDetailsLoading && !licenseDetailsError && licenseDetailsData != null && (() => {
                        const cloudName = licenseDetailsCloudName || licenseDetailsData?.request?.cloudName || licenseDetailsData?.cloudName || '';
                        const rawFiles = licenseDetailsData?.files ?? licenseDetailsData?.request?.files ?? (Array.isArray(licenseDetailsData) ? licenseDetailsData : []);
                        const getFileDateRaw = (obj) => {
                            if (obj == null || typeof obj !== 'object') return null;
                            const raw = obj.lastModified ?? obj.last_modified ?? obj.date ?? obj.createdAt ?? obj.created_at ?? obj.timestamp ?? obj.uploadedAt ?? obj.uploaded_at ?? '';
                            if (!raw) return null;
                            const d = typeof raw === 'string' ? new Date(raw) : raw instanceof Date ? raw : (typeof raw === 'number' && raw < 1e12 ? new Date(raw * 1000) : new Date(raw));
                            return Number.isNaN(d.getTime()) ? null : d;
                        };
                        const formatFileDateTable = (obj) => {
                            const d = getFileDateRaw(obj);
                            if (!d) return '';
                            const day = String(d.getDate()).padStart(2, '0');
                            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                            const mmm = months[d.getMonth()];
                            const yyyy = d.getFullYear();
                            const hh = String(d.getHours()).padStart(2, '0');
                            const mm = String(d.getMinutes()).padStart(2, '0');
                            const ss = String(d.getSeconds()).padStart(2, '0');
                            return `${day}-${mmm}-${yyyy} ${hh}:${mm}:${ss}`;
                        };
                        const filesWithMeta = (Array.isArray(rawFiles) ? rawFiles : []).map((f) => {
                            const fileName = (f?.fileName ?? f?.name ?? f?.filename ?? (typeof f === 'string' ? f : '')).toString().trim();
                            const fileObj = typeof f === 'object' && f !== null ? f : {};
                            const dateWithTime = formatFileDateTable(fileObj);
                            const fileTime = getFileDateRaw(fileObj) ? getFileDateRaw(fileObj).getTime() : 0;
                            return { fileName, dateWithTime, fileTime };
                        }).filter((f) => f.fileName);
                        const maxTime = filesWithMeta.length ? Math.max(...filesWithMeta.map((f) => f.fileTime)) : 0;
                        const files = filesWithMeta.map((f) => ({
                            ...f,
                            isLatest: f.fileTime > 0 && f.fileTime === maxTime
                        }));
                        return (
                            <>
                                <Segment style={{ marginBottom: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                    <Header as="h5" style={{ marginTop: 0, marginBottom: '0.25rem', color: '#0f172a', fontSize: '1.1rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem' }}>
                                        <Icon name="folder open" style={{ color: '#3b82f6' }} /> License files
                                    </Header>
                                    <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '0 0 0.75rem 0' }}>
                                        View or download license files for this cloud.
                                    </p>
                                    {files.length > 0 ? (
                                        <div style={{ overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                                            <table className="ui celled compact small table" style={{ margin: 0, width: '100%', minWidth: 0 }}>
                                                <colgroup>
                                                    <col style={{ minWidth: 0 }} />
                                                    <col style={{ width: '1%', whiteSpace: 'nowrap' }} />
                                                    <col style={{ width: '1%', whiteSpace: 'nowrap' }} />
                                                </colgroup>
                                                <thead>
                                                    <tr>
                                                        <th style={{ background: '#f8fafc', fontWeight: 600, color: '#475569', padding: '10px 14px', borderBottom: '1px solid #e2e8f0' }}>File name</th>
                                                        <th style={{ background: '#f8fafc', fontWeight: 600, color: '#475569', padding: '10px 14px', borderBottom: '1px solid #e2e8f0' }}>Date</th>
                                                        <th style={{ background: '#f8fafc', fontWeight: 600, color: '#475569', padding: '10px 14px', borderBottom: '1px solid #e2e8f0' }}>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {files.map((f, idx) => (
                                                        <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                                            <td style={{ verticalAlign: 'middle', color: '#1e293b', padding: '10px 14px', borderTop: '1px solid #e2e8f0', wordBreak: 'break-word' }}>
                                                                <Icon name="file" style={{ marginRight: '0.5rem', color: '#64748b' }} />
                                                                {f.fileName}
                                                                {f.isLatest && (
                                                                    <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', fontWeight: 700, color: '#047857', background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)', padding: '2px 8px', borderRadius: '6px', border: '1px solid #059669', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Latest</span>
                                                                )}
                                                            </td>
                                                            <td style={{ verticalAlign: 'middle', color: '#475569', padding: '10px 14px', borderTop: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                                                                {f.dateWithTime || '—'}
                                                            </td>
                                                            <td style={{ verticalAlign: 'middle', padding: '10px 14px', borderTop: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}>
                                                                    <Button
                                                                        size="small"
                                                                        icon="eye"
                                                                        content="View License"
                                                                        onClick={() => openLicenseFileView(cloudName, f.fileName)}
                                                                    />
                                                                    <Button
                                                                        size="small"
                                                                        primary={f.isLatest}
                                                                        icon="download"
                                                                        content="Download License"
                                                                        loading={licenseDetailsDownloadingFileName === f.fileName}
                                                                        disabled={!!licenseDetailsDownloadingFileName}
                                                                        onClick={() =>
                                                                            f.isLatest
                                                                                ? handleLicenseFileDownload(cloudName, f.fileName)
                                                                                : requestOldLicenseDownload(cloudName, f.fileName)
                                                                        }
                                                                        style={
                                                                            f.isLatest
                                                                                ? undefined
                                                                                : {
                                                                                      background: '#e0f2fe',
                                                                                      color: '#1d4ed8',
                                                                                      borderColor: '#bfdbfe'
                                                                                  }
                                                                        }
                                                                    />
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <p style={{ color: '#64748b', margin: 0 }}>No license files listed.</p>
                                    )}
                                </Segment>
                            </>
                        );
                    })()}
                </Modal.Content>
                <Modal.Actions style={{ borderTop: '1px solid #e5e7eb', padding: '1rem 1.5rem' }}>
                    <Button onClick={closeLicenseDetailsModal}>Close</Button>
                </Modal.Actions>
            </Modal>

            {/* License file viewer – same design pattern as View details */}
            <Modal
                size="tiny"
                open={confirmOldDownloadOpen}
                onClose={() => handleConfirmOldDownload(false)}
                style={{ top: '50%', transform: 'translateY(-50%)' }}
            >
                <Modal.Header>Download an Older License</Modal.Header>
                <Modal.Content>
                    <p>
                    A more recently generated license is available. You are about to download an older license. Are you sure you wish to continue?
                    </p>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => handleConfirmOldDownload(false)}>
                        Cancel
                    </Button>
                    <Button primary onClick={() => handleConfirmOldDownload(true)}>
                        Download An Older License
                    </Button>
                </Modal.Actions>
            </Modal>

            {/* License file viewer – same design pattern as View details */}
            <Modal
                open={fileViewerOpen}
                onClose={closeLicenseFileView}
                size="large"
            >
                <Modal.Header>
                    View License
                    {!fileViewerHideHeaderFileName && fileViewerFileName && (
                        <span style={{ fontWeight: 'normal', marginLeft: '0.5rem', color: '#666' }}>
                            File: {fileViewerFileName}
                        </span>
                    )}
                </Modal.Header>
                <Modal.Content>
                    {fileViewerLoading && (
                        <Dimmer active inverted>
                            <Loader>Loading file...</Loader>
                        </Dimmer>
                    )}
                    {fileViewerError && (
                        <Message negative>
                            <Message.Header>Error</Message.Header>
                            <p>{fileViewerError}</p>
                        </Message>
                    )}
                    {!fileViewerLoading && !fileViewerError && fileViewerBlobUrl && (
                        <>
                            {fileViewerText != null ? (
                                <>
                                    <div style={{ marginBottom: '0.75rem', display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            size="small"
                                            toggle
                                            active={fileViewerMode === 'raw'}
                                            onClick={() => setFileViewerMode((v) => (v === 'raw' ? 'readable' : 'raw'))}
                                        >
                                            <Icon name="code" />
                                            {fileViewerMode === 'raw' ? 'Show Formatted' : 'View raw data'}
                                        </Button>
                                    </div>
                                    {fileViewerMode === 'raw' ? (
                                        <div
                                            style={{
                                                margin: 0,
                                                borderRadius: '8px',
                                                overflow: 'hidden',
                                                maxHeight: '70vh',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                                                border: '1px solid #333'
                                            }}
                                        >
                                            <div
                                                style={{
                                                    padding: '8px 12px',
                                                    background: '#2d2d2d',
                                                    borderBottom: '1px solid #444',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    fontFamily: 'monospace',
                                                    fontSize: '12px',
                                                    color: '#9ca3af'
                                                }}
                                            >
                                                <span>Raw data</span>
                                                <span style={{ color: '#6b7280', marginLeft: 4 }}>
                                                    — {parseLicenseFileText(fileViewerText) ? 'response.json' : (fileViewerFileName || 'file')}
                                                </span>
                                                <span
                                                    style={{ marginLeft: 'auto', cursor: 'pointer', color: fileViewerRawCopied ? '#22c55e' : '#9ca3af' }}
                                                    onClick={() => {
                                                        safeCopyToClipboard(fileViewerText || '');
                                                        setFileViewerRawCopied(true);
                                                        setTimeout(() => setFileViewerRawCopied(false), 1000);
                                                    }}
                                                    role="button"
                                                    aria-label="Copy raw data"
                                                >
                                                    <Icon name={fileViewerRawCopied ? 'check' : 'copy'} />
                                                </span>
                                            </div>
                                            <pre
                                                style={{
                                                    margin: 0,
                                                    padding: '14px 16px',
                                                    background: '#1a1a1a',
                                                    color: '#d4d4d4',
                                                    fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
                                                    fontSize: '12px',
                                                    lineHeight: 1.5,
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                    overflow: 'auto',
                                                    flex: 1,
                                                    minHeight: 120
                                                }}
                                            >
                                                {fileViewerText}
                                            </pre>
                                        </div>
                                    ) : (
                                        (() => {
                                            const parsed = parseLicenseFileText(fileViewerText);
                                            if (parsed !== null && typeof parsed === 'object') {
                                                let detailsLikeData =
                                                    parsed?.licenseRequest != null ||
                                                    parsed?.request != null ||
                                                    parsed?.generatedLicense != null ||
                                                    parsed?.generated != null
                                                        ? parsed
                                                        : { generatedLicense: parsed };
                                                if (licenseDetailsCloudSizeAllowed != null || fileViewerForceByosUnlimited) {
                                                    detailsLikeData = {
                                                        ...detailsLikeData,
                                                        generatedLicense: {
                                                            ...(detailsLikeData?.generatedLicense && typeof detailsLikeData.generatedLicense === 'object'
                                                                ? detailsLikeData.generatedLicense
                                                                : {}),
                                                            cloudSizeAllowed: fileViewerForceByosUnlimited
                                                                ? 'Unlimited'
                                                                : normalizeCloudSizeAllowedValue(licenseDetailsCloudSizeAllowed)
                                                        }
                                                    };
                                                }
                                                return <LicenseRequestDetailsDesign data={detailsLikeData} />;
                                            }
                                            return (
                                                <Segment style={{ marginBottom: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                                    <Header as="h5" style={{ marginTop: 0, marginBottom: '0.75rem', color: '#0f172a', fontSize: '1.1rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem' }}>
                                                        <Icon name="file text" style={{ color: '#3b82f6' }} /> License file
                                                    </Header>
                                                    <pre style={{ margin: 0, padding: '14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#334155', maxHeight: '55vh', overflowY: 'auto' }}>
                                                        {fileViewerText}
                                                    </pre>
                                                </Segment>
                                            );
                                        })()
                                    )}
                                </>
                            ) : (
                                <div style={{ maxHeight: '70vh', overflow: 'hidden', borderRadius: '8px', border: '1px solid #333' }}>
                                    <iframe
                                        title={fileViewerFileName || 'License file'}
                                        src={fileViewerBlobUrl}
                                        style={{ width: '100%', height: '70vh', border: 'none', display: 'block' }}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={closeLicenseFileView}>Close</Button>
                </Modal.Actions>
            </Modal>
        </div>
    );
};

export default LicenseGenerator;

